import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import TrDateInput from '../components/TrDateInput';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import {
  AramaKriterleri, bosKriterler, parseAramaKriterleri, zenginlestirKriterler,
  kriterEtiketleri, KriterEtiketi,
} from '../utils/daireAramaParser';
import { eslestir, AramaSonucu } from '../utils/daireAramaEslestirici';
import {
  KalemTuru, KALEM_TURLERI, ODEME_TURLERI, kalemTuruBul, planiDuzenle, kalemTuruDegistir,
} from '../utils/odemeKalemTuru';
import { useAuth } from '../contexts/AuthContext';
import {
  FiyatlandirmaKurali, kontrolEt, kuralAktifMi, tarihIzinliMi, sonOdemeTarihiMesaji,
} from '../utils/fiyatlandirmaKontrol';
import FiyatAraliklariModal from '../components/FiyatAraliklariModal';
import FiyatKontrolUyari from '../components/FiyatKontrolUyari';

const API_BASE_URL = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

interface OdemeKalem {
  kalemID: number | null;
  vadeTarihi: string;
  miktar: string;
  aciklama: string;
  odemeTuru?: string;
  kilitli?: boolean;
}

interface TeklifTab {
  teklifID: number | null;
  teklifAdi: string;
  siraNo: number;
  teklifMiktari: string;
  kalemler: OdemeKalem[];
}

interface OdemePlanSablonDetayi {
  detayId: string;
  odemeTuru: string;
  vadeAyEndeksi: number;
  hesaplamaYontemi: string;
  deger: number;
}

interface OdemePlanSablonu {
  sablonId: string;
  sablonAdi: string;
  pesinatTipi: string;
  pesinatDegeri: number;
  vadeTipi: string;
  maksimumTaksitSayisi: number;
  kalanBakiyeDagilimTipi: string;
  kurusYuvarlamaTipi: string;
  isActive: boolean;
  detaylar: OdemePlanSablonDetayi[];
}

interface SunumBelge {
  sunumBelgeId: number;
  takipNo: string;
  aciklama: string;
  sunumBelgeTuru: string;
  sunumBelgeAdi: string;
}

interface GVGorunumDaire {
  gorunumDaireID: number;
  daireID: number;
  xYuzde: number;
  yYuzde: number;
  rxMin?: number | null;
  ryMin?: number | null;
  rxMax?: number | null;
  ryMax?: number | null;
}
interface GVGorunum {
  gorunumID: number;
  gorunumAdi: string;
  aciklama?: string;
  arkaplanGorsel?: string;
  daireler: GVGorunumDaire[];
}

interface PaylasimDokuman {
  id: number;
  dokumanID: number;
  baslik: string;
  aciklama?: string;
  dokumanTipi: string;
  dosyaYolu?: string;
  videoEmbedKodu?: string;
  orijinalDosyaAdi?: string;
  etiketler: {ad: string; renk: string}[];
}

interface DokumanItem {
  dokumanID: number;
  baslik: string;
  aciklama?: string;
  dokumanTipi: string;
  dosyaYolu?: string;
  videoEmbedKodu?: string;
  etiketler: {etiketID: number; ad: string; renk: string}[];
}

interface SunumDaireDetay {
  sunumDaireID: number;
  sunumOgeTuru: 'Daire' | 'Portfoy';
  daireID: number | null;
  portfoyID: number | null;
  gayrimenkulID: number;
  durum: string;
  musteriTepkisi: string;
  not: string | null;
  musteriDurumu?: string | null;
  musteriNotu?: string | null;
  eklenmeTarihi: string;
  guncellemeTarihi: string | null;
  daire: {
    daireID: number;
    daireNo: string;
    takipNo: string;
    projeID: number | null;
    projeTakipNo: string | null;
    projeAdi: string | null;
    blokAdi: string | null;
    katAdi: string | null;
    cepheAdi: string | null;
    daireTipiAdi: string | null;
    listeFiyati: number | null;
    netM2: number | null;
    brutM2: number | null;
    daireDurumu: string;
  } | null;
  portfoy: {
    portfoyID: number;
    baslik: string;
    adres: string | null;
    fiyat: number;
    m2: number;
    ilanNo: string;
    tur: string;
    durum: string;
    vitrinFoto: string | null;
  } | null;
}

interface PortfoyItem {
  portfoyID: number;
  baslik: string;
  adres: string | null;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  aktifMi: boolean;
  aciklama: string | null;
  ozellikler: { ozellikID: number; ozellikAdi: string }[];
  fotograflar: { fotoID: number; dosyaYolu: string; isVitrin: boolean }[];
}

interface PortfoyDetayData extends PortfoyItem {
  iletisimler: { iletisimID: number; adSoyad: string; telefon: string | null; eposta: string | null }[];
}

interface MusteriSunumDetayData {
  sunumID: number;
  takipNo: string;
  cariID: number;
  cari: {
    cariID: number;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
  } | null;
  olusturanKullaniciID: number;
  olusturanAdSoyad: string | null;
  baslik: string | null;
  durum: string;
  olusturmaTarihi: string;
  guncellemeTarihi: string | null;
  aktifMi: boolean;
  daireler: SunumDaireDetay[];
}

interface DaireItem {
  daireID: number;
  daireNo: string;
  daireDurumu: string;
  listeFiyati: number | null;
  netM2: number | null;
  brutM2: number | null;
  proje: { projeID: number; projeAdi: string; projeDurumu?: string; sehir?: string | null } | null;
  blok: { blokID: number; blokAdi: string } | null;
  kat: { katID: number; katAdi: string } | null;
  cephe: { cepheID: number; cepheAdi: string } | null;
  manzara?: { manzaraID: number; manzaraAdi: string } | null;
  daireTipi?: { daireTipID: number; tip: string } | null;
}

const DURUM_OPTIONS = ['Taslak', 'Devam Ediyor', 'Tamamlandı', 'Olumlu Sonuçlandı', 'Olumsuz Sonuçlandı', 'İptal Edildi'];

const DAIRE_DURUM_OPTIONS = ['Bekliyor', 'Gösterildi', 'Beğenildi', 'Reddedildi'];

const TEPKI_OPTIONS = ['Karar Vermedi', 'Beğendi', 'İlgileniyor', 'Fiyat Yüksek', 'Uygun Değil', 'Almak İstiyor'];

/* ---------- Sunulan daireler sıralaması ----------
 * Üç bant: (0) hiç işlem yapılmamışlar en üstte, (1) işlem görmüşler olumluluk
 * puanına göre azalan, (2) olumsuz sinyal taşıyanlar en altta.
 * Puan; sunum durumu + müşteri tepkisi + portal müşteri görüşünden hesaplanır.  */
const SUNUM_DURUM_PUAN: Record<string, number> = {
  'Beğenildi': 3,
  'Gösterildi': 1,
  'Bekliyor': 0,
  'Reddedildi': -4,
};

const TEPKI_PUAN: Record<string, number> = {
  'Almak İstiyor': 5,
  'Beğendi': 3,
  'İlgileniyor': 2,
  'Karar Vermedi': 0,
  'Fiyat Yüksek': -2,
  'Uygun Değil': -4,
};

const MUSTERI_GORUS_PUAN: Record<string, number> = {
  'Olumlu': 4,
  'Olumsuz': -4,
};

const olumlulukPuani = (sd: SunumDaireDetay): number =>
  (SUNUM_DURUM_PUAN[sd.durum] ?? 0) +
  (TEPKI_PUAN[sd.musteriTepkisi] ?? 0) +
  (sd.musteriDurumu ? (MUSTERI_GORUS_PUAN[sd.musteriDurumu] ?? 0) : 0);

/** Hiç dokunulmamış: sunum durumu ve tepki varsayılanda, portalden görüş/not gelmemiş. */
const islemYapilmamisMi = (sd: SunumDaireDetay): boolean =>
  sd.durum === 'Bekliyor' &&
  sd.musteriTepkisi === 'Karar Vermedi' &&
  !sd.musteriDurumu &&
  !sd.musteriNotu;

/** Olumsuz sinyal: reddedildi, olumsuz tepki ya da portalde olumsuz görüş. */
const olumsuzMu = (sd: SunumDaireDetay): boolean =>
  sd.durum === 'Reddedildi' ||
  sd.musteriTepkisi === 'Fiyat Yüksek' ||
  sd.musteriTepkisi === 'Uygun Değil' ||
  sd.musteriDurumu === 'Olumsuz';

const siralamaBandi = (sd: SunumDaireDetay): number =>
  olumsuzMu(sd) ? 2 : islemYapilmamisMi(sd) ? 0 : 1;

const sunumDaireleriSirala = (liste: SunumDaireDetay[]): SunumDaireDetay[] =>
  [...liste].sort((a, b) => {
    const ba = siralamaBandi(a), bb = siralamaBandi(b);
    if (ba !== bb) return ba - bb;
    const pa = olumlulukPuani(a), pb = olumlulukPuani(b);
    if (pa !== pb) return pb - pa;
    return a.sunumDaireID - b.sunumDaireID;
  });

const DURUM_BADGE: Record<string, { bg: string; color: string }> = {
  'Taslak':             { bg: '#f1f5f9', color: '#475569' },
  'Devam Ediyor':       { bg: '#eff6ff', color: '#1d4ed8' },
  'Tamamlandı':         { bg: '#f0fdf4', color: '#15803d' },
  'Olumlu Sonuçlandı':  { bg: '#f0fdf4', color: '#059669' },
  'Olumsuz Sonuçlandı': { bg: '#fff1f2', color: '#be123c' },
  'İptal Edildi':       { bg: '#fef3c7', color: '#92400e' },
};

const TEPKI_BADGE: Record<string, { bg: string; color: string }> = {
  'Karar Vermedi': { bg: '#f1f5f9', color: '#475569' },
  'Beğendi':       { bg: '#f0fdf4', color: '#15803d' },
  'İlgileniyor':   { bg: '#eff6ff', color: '#1d4ed8' },
  'Fiyat Yüksek':  { bg: '#fff7ed', color: '#c2410c' },
  'Uygun Değil':   { bg: '#fff1f2', color: '#be123c' },
  'Almak İstiyor': { bg: '#f0fdf4', color: '#059669' },
};

// Dairenin GERÇEK (proje/portföydeki) güncel durumu — sunuma eklendikten sonra
// başka bir sözleşmeyle satılmış/rezerve edilmiş/tahsis edilmiş olabilir.
const DAIRE_GUNCEL_DURUM_META: Record<string, { bg: string; rowBg: string; color: string; dot: string; label: string }> = {
  'Satışta':    { bg: '#f0fdf4', rowBg: '#ffffff', color: '#15803d', dot: '#22c55e', label: 'Satışta' },
  'Rezerve':    { bg: '#fff7ed', rowBg: '#fffbf5', color: '#c2410c', dot: '#f97316', label: 'Rezerve Edildi' },
  'Sözleşme':   { bg: '#eff6ff', rowBg: '#f7faff', color: '#1d4ed8', dot: '#3b82f6', label: 'Satıldı (Sözleşme)' },
  'ArsaSahibi': { bg: '#f1f5f9', rowBg: '#f8fafc', color: '#475569', dot: '#94a3b8', label: 'Arsa Sahibi' },
  'Taşeron':    { bg: '#f0f9ff', rowBg: '#f7fcff', color: '#0369a1', dot: '#0ea5e9', label: 'Taşeron (Barter)' },
};

// Teklif Yazdır seçenekleri: son seçim tarayıcıda hatırlanır (varsayılan: liste fiyatı açık, ortalama vade kapalı)
interface TeklifYazdirSecenek { listeFiyati: boolean; ortalamaVade: boolean; }
const TEKLIF_YAZDIR_ANAHTAR = 'teklifYazdirSecenekleri';
const teklifYazdirSecenekOku = (): TeklifYazdirSecenek => {
  try {
    const v = JSON.parse(localStorage.getItem(TEKLIF_YAZDIR_ANAHTAR) || 'null');
    if (v && typeof v === 'object') return { listeFiyati: v.listeFiyati !== false, ortalamaVade: v.ortalamaVade === true };
  } catch { /* yok say */ }
  return { listeFiyati: true, ortalamaVade: false };
};
const teklifYazdirSecenekYaz = (v: TeklifYazdirSecenek) => {
  try { localStorage.setItem(TEKLIF_YAZDIR_ANAHTAR, JSON.stringify(v)); } catch { /* yok say */ }
};

const MusteriSunumDetay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sunum, setSunum] = useState<MusteriSunumDetayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingDurum, setSavingDurum] = useState(false);
  const [updatingDaire, setUpdatingDaire] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [daireNotlar, setDaireNotlar] = useState<Record<number, string>>({});

  // Daire ekleme modal state
  const [showAddDaireModal, setShowAddDaireModal] = useState(false);
  const [addOgeTuru, setAddOgeTuru] = useState<'Daire' | 'Portfoy' | 'Akilli'>('Daire');
  const [tumDaireler, setTumDaireler] = useState<DaireItem[]>([]);
  const [addFilterProjeID, setAddFilterProjeID] = useState('');
  const [addFilterKatID, setAddFilterKatID] = useState('');
  const [addFilterCepheID, setAddFilterCepheID] = useState('');
  const [addFilterBlokID, setAddFilterBlokID] = useState('');
  const [addFilterManzaraID, setAddFilterManzaraID] = useState('');
  const [addFilterTipID, setAddFilterTipID] = useState('');
  const [addDaireSearch, setAddDaireSearch] = useState('');
  const [selectedDaireIDs, setSelectedDaireIDs] = useState<number[]>([]);
  const [tumPortfoyler, setTumPortfoyler] = useState<PortfoyItem[]>([]);
  const [addPortfoySearch, setAddPortfoySearch] = useState('');
  const [selectedPortfoyIDs, setSelectedPortfoyIDs] = useState<number[]>([]);

  // Akıllı Arama state
  const [akilliMetin, setAkilliMetin] = useState('');
  const [akilliLoading, setAkilliLoading] = useState(false);
  const [akilliKriterler, setAkilliKriterler] = useState<AramaKriterleri | null>(null);
  const [akilliKaynak, setAkilliKaynak] = useState<'ai' | 'yerel' | null>(null);
  const [akilliHata, setAkilliHata] = useState<string | null>(null);

  // Portföy detay (görsel + özellik) modalı
  const [portfoyDetay, setPortfoyDetay] = useState<PortfoyDetayData | null>(null);
  const [portfoyDetayLoading, setPortfoyDetayLoading] = useState(false);
  const [portfoyAktifFoto, setPortfoyAktifFoto] = useState(0);
  const [addingDaireler, setAddingDaireler] = useState(false);
  const [daireDetayIndex, setDaireDetayIndex] = useState<number | null>(null);
  const [daireDetayTab, setDaireDetayTab] = useState<'galeri' | 'gorunum'>('galeri');
  const [daireGorunumlar, setDaireGorunumlar] = useState<GVGorunum[]>([]);
  const [gorunumLoading, setGorunumLoading] = useState(false);
  const [hoveredGorunumDaireID, setHoveredGorunumDaireID] = useState<number | null>(null);
  const [selectedGorunumIdx, setSelectedGorunumIdx] = useState(0);
  const [galeriGorseller, setGaleriGorseller] = useState<SunumBelge[]>([]);
  const [galeriBelgeler, setGaleriBelgeler] = useState<SunumBelge[]>([]);
  const [galeriVideolar, setGaleriVideolar] = useState<SunumBelge[]>([]);
  const [galeriSlideIndex, setGaleriSlideIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState<SunumBelge | null>(null);

  // Ödeme Planı Modal
  const [showOdemePlaniModal, setShowOdemePlaniModal] = useState<{ sunumDaireID: number; gayrimenkulID: number; baslik: string; listeFiyati: number | null; ogeTuru: 'Daire' | 'Portfoy'; projeID: number | null } | null>(null);
  const { user } = useAuth();
  const [fiyatKurali, setFiyatKurali] = useState<FiyatlandirmaKurali | null>(null);
  const [showFiyatAraliklari, setShowFiyatAraliklari] = useState(false);
  const [odemePlaniTeklifler, setOdemePlaniTeklifler] = useState<TeklifTab[]>([]);
  const [aktifTeklifTab, setAktifTeklifTab] = useState(0);
  const [odemePlaniLoading, setOdemePlaniLoading] = useState(false);
  const [odemePlaniSaving, setOdemePlaniSaving] = useState(false);
  const [topluTaksitSayisi, setTopluTaksitSayisi] = useState('12');
  const [topluIlkTarih, setTopluIlkTarih] = useState('');
  const [topluAciklama, setTopluAciklama] = useState('');
  const [topluPlanTutari, setTopluPlanTutari] = useState('');
  const [topluOdemeTuru, setTopluOdemeTuru] = useState('Nakit');
  const [odemePlanSablonlari, setOdemePlanSablonlari] = useState<OdemePlanSablonu[]>([]);
  const [seciliSablonId, setSeciliSablonId] = useState('');
  const [sablonOdemeTuru, setSablonOdemeTuru] = useState('Nakit');
  const [yeniKalem, setYeniKalem] = useState<{ vadeTarihi: string; miktar: string; tur: KalemTuru; odemeTuru: string; aciklama: string }>(
    { vadeTarihi: '', miktar: '', tur: 'Taksit', odemeTuru: 'Nakit', aciklama: '' }
  );
  const [teklifYazdiriliyor, setTeklifYazdiriliyor] = useState(false);
  const [teklifYazdirModal, setTeklifYazdirModal] = useState(false);
  const [teklifYazdirSecenek, setTeklifYazdirSecenek] = useState<TeklifYazdirSecenek>(teklifYazdirSecenekOku);
  const [sozlesmeYapiliyor, setSozlesmeYapiliyor] = useState(false);
  const [fiyatlamaAyari, setFiyatlamaAyari] = useState<{ aktifMi: boolean; aylikArtisOrani: number; kabulEdilenOrtalamaVadeGun: number } | null>(null);

  // Müşteri Linki Modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkler, setLinkler] = useState<{linkID: number; linkToken: string; olusturmaTarihi: string; gecerlilikTarihi: string; sureGectiMi: boolean; portalUrl: string}[]>([]);
  const [linklerLoading, setLinklerLoading] = useState(false);
  const [yeniLinkGun, setYeniLinkGun] = useState('3');
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);
  const [qrVisibleLinkId, setQrVisibleLinkId] = useState<number | null>(null);

  // Online Görüşme
  const [activeGorusme, setActiveGorusme] = useState<{gorusmeID: number; odaAdi: string; baslamaTarihi: string} | null>(null);
  const [gorusmeLoading, setGorusmeLoading] = useState(false);
  const activeGorusmeRef = useRef<number | null>(null);

  // Döküman Paylaşım
  const [showDokumanPaylas, setShowDokumanPaylas] = useState(false);
  const [paylasimDokumanlar, setPaylasimDokumanlar] = useState<PaylasimDokuman[]>([]);
  const [tumDokumanlar, setTumDokumanlar] = useState<DokumanItem[]>([]);
  const [seciliDokumanIDs, setSeciliDokumanIDs] = useState<number[]>([]);
  const [dokumanEtiketFiltre, setDokumanEtiketFiltre] = useState<number | ''>('');
  const [tumEtiketler, setTumEtiketler] = useState<{etiketID: number; ad: string; renk: string}[]>([]);
  const [dokumanPaylasimSaving, setDokumanPaylasimSaving] = useState(false);
  const [dokumanBelgeViewer, setDokumanBelgeViewer] = useState<string | null>(null);

  // Binlik ayıraç yardımcı fonksiyonları
  const formatCurrency = (value: string): string => {
    const num = value.replace(/[^\d]/g, '');
    if (!num) return '';
    return parseInt(num).toLocaleString('tr-TR');
  };

  const parseCurrency = (value: string): string => {
    return value.replace(/[^\d]/g, '');
  };

  const fetchPaylasimDokumanlar = useCallback(async (sunumID: number) => {
    try {
      const res = await axiosInstance.get(`/api/SunumPaylasimDokumanlar/${sunumID}`);
      setPaylasimDokumanlar(res.data);
    } catch { /* sessiz */ }
  }, []);

  const openDokumanModal = async () => {
    if (!sunum) return;
    setShowDokumanPaylas(true);
    try {
      const [dokRes, etiketRes, paylasimRes] = await Promise.all([
        axiosInstance.get('/api/Dokumanlar'),
        axiosInstance.get('/api/Etiketler'),
        axiosInstance.get(`/api/SunumPaylasimDokumanlar/${sunum.sunumID}`),
      ]);
      setTumDokumanlar(dokRes.data);
      setTumEtiketler(etiketRes.data);
      setPaylasimDokumanlar(paylasimRes.data);
      setSeciliDokumanIDs((paylasimRes.data as PaylasimDokuman[]).map(p => p.dokumanID));
    } catch { toast.error(t('msg.islemHatasi')); }
  };

  const handleDokumanPaylasimKaydet = async () => {
    if (!sunum) return;
    setDokumanPaylasimSaving(true);
    try {
      await axiosInstance.post(`/api/SunumPaylasimDokumanlar/${sunum.sunumID}`, seciliDokumanIDs);
      await fetchPaylasimDokumanlar(sunum.sunumID);
      toast.success(t('msg.islemBasarili'));
      setShowDokumanPaylas(false);
    } catch { toast.error(t('msg.islemHatasi')); }
    finally { setDokumanPaylasimSaving(false); }
  };

  const fetchSunum = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/MusteriSunumlari/${id}`);
      setSunum(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSunum();
  }, [fetchSunum]);

  // Ref'i activeGorusme ile senkron tut — cleanup'ta stale closure sorununu önler
  useEffect(() => {
    activeGorusmeRef.current = activeGorusme?.gorusmeID ?? null;
  }, [activeGorusme]);

  // Sayfa kapatılır veya component unmount olursa aktif görüşmeyi sonlandır
  useEffect(() => {
    const bitirGorusme = () => {
      const gorusmeID = activeGorusmeRef.current;
      if (!gorusmeID) return;
      // navigator.sendBeacon ile fire-and-forget (sayfa kapanırken de çalışır)
      const token = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}').token; } catch { return null; }
      })();
      const url = `${axiosInstance.defaults.baseURL}/api/OnlineGorusmeler/${gorusmeID}/bitir`;
      const blob = new Blob([], { type: 'application/json' });
      if (token) {
        fetch(url, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` }, keepalive: true })
          .catch(() => { navigator.sendBeacon(url, blob); });
      }
    };
    window.addEventListener('beforeunload', bitirGorusme);
    return () => {
      window.removeEventListener('beforeunload', bitirGorusme);
      bitirGorusme();
    };
  }, []);

  useEffect(() => {
    if (sunum) {
      const init: Record<number, string> = {};
      sunum.daireler.forEach(d => { init[d.sunumDaireID] = d.not || ''; });
      setDaireNotlar(init);
      fetchPaylasimDokumanlar(sunum.sunumID);
    }
  }, [sunum]);

  // Sunulan daireler sıralaması: işlem görmemişler üstte, olumsuzlar altta,
  // arada olumluluk puanı yüksek olanlar önce. Detay modalı da bu sırayı izler.
  const siraliDaireler: SunumDaireDetay[] = React.useMemo(
    () => sunumDaireleriSirala(sunum?.daireler ?? []),
    [sunum]
  );

  useEffect(() => {
    if (daireDetayIndex === null || !sunum) {
      setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]);
      return;
    }
    const sd = siraliDaireler[daireDetayIndex];
    if (!sd?.daire?.takipNo) { setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]); return; }
    setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]); setGaleriSlideIndex(0);
    axiosInstance.get(`/api/SunumBelgeleri/takip/${sd.daire.takipNo}`)
      .then(res => {
        const daireBelgeler: SunumBelge[] = res.data || [];
        setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
        setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
        setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
        setGaleriSlideIndex(0);
      })
      .catch(() => { setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]); });
  }, [daireDetayIndex, sunum]);

  useEffect(() => {
    setDaireDetayTab('galeri');
    setDaireGorunumlar([]);
    setHoveredGorunumDaireID(null);
    setSelectedGorunumIdx(0);
  }, [daireDetayIndex]);

  const handleSunumDurumChange = async (yeniDurum: string) => {
    if (!sunum) return;
    try {
      setSavingDurum(true);
      await axiosInstance.put(`/api/MusteriSunumlari/${sunum.sunumID}`, { durum: yeniDurum });
      setSunum(prev => prev ? { ...prev, durum: yeniDurum } : prev);
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setSavingDurum(false);
    }
  };

  const handleDaireFieldChange = async (sunumDaireID: number, field: 'durum' | 'musteriTepkisi', value: string) => {
    if (!sunum) return;
    try {
      setUpdatingDaire(sunumDaireID);
      const payload = field === 'durum' ? { durum: value } : { musteriTepkisi: value };
      await axiosInstance.put(`/api/MusteriSunumlari/${sunum.sunumID}/daireler/${sunumDaireID}`, payload);
      setSunum(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          daireler: prev.daireler.map(d =>
            d.sunumDaireID === sunumDaireID ? { ...d, [field]: value } : d
          ),
        };
      });
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setUpdatingDaire(null);
    }
  };

  const handleRemoveDaire = async (sunumDaireID: number) => {
    if (!sunum) return;
    try {
      await axiosInstance.delete(`/api/MusteriSunumlari/${sunum.sunumID}/daireler/${sunumDaireID}`);
      setSunum(prev => prev ? { ...prev, daireler: prev.daireler.filter(d => d.sunumDaireID !== sunumDaireID) } : prev);
      setDeleteConfirm(null);
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDaireSaveNot = async (sunumDaireID: number) => {
    if (!sunum) return;
    const currentNot = sunum.daireler.find(d => d.sunumDaireID === sunumDaireID)?.not || '';
    const newNot = daireNotlar[sunumDaireID] ?? '';
    if (newNot === currentNot) return;
    try {
      setUpdatingDaire(sunumDaireID);
      await axiosInstance.put(`/api/MusteriSunumlari/${sunum.sunumID}/daireler/${sunumDaireID}`, { not: newNot });
      setSunum(prev => {
        if (!prev) return prev;
        return { ...prev, daireler: prev.daireler.map(d => d.sunumDaireID === sunumDaireID ? { ...d, not: newNot } : d) };
      });
    } catch {
      toast.error('Not kaydedilemedi.');
    } finally {
      setUpdatingDaire(null);
    }
  };

  const openAddDaireModal = async (baslangicSekmesi: 'Daire' | 'Portfoy' | 'Akilli' = 'Daire') => {
    setShowAddDaireModal(true);
    setAddOgeTuru(baslangicSekmesi);
    setSelectedDaireIDs([]);
    setSelectedPortfoyIDs([]);
    setAddFilterProjeID('');
    setAddFilterKatID('');
    setAddFilterCepheID('');
    setAddFilterBlokID('');
    setAddFilterManzaraID('');
    setAddFilterTipID('');
    setAddDaireSearch('');
    setAddPortfoySearch('');
    setAkilliMetin('');
    setAkilliKriterler(null);
    setAkilliKaynak(null);
    setAkilliHata(null);
    setAkilliLoading(false);
    try {
      const [daireRes, portfoyRes] = await Promise.all([
        axiosInstance.get('/api/Daireler?aktifMi=true'),
        axiosInstance.get('/api/EmlakPortfoy?aktifMi=true').catch(() => ({ data: [] })),
      ]);
      const satistakiler = (daireRes.data as DaireItem[]).filter(d => d.daireDurumu === 'Satışta');
      setTumDaireler(satistakiler);
      setTumPortfoyler(portfoyRes.data as PortfoyItem[]);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleAddDaireler = async () => {
    if (!sunum || (selectedDaireIDs.length === 0 && selectedPortfoyIDs.length === 0)) return;
    try {
      setAddingDaireler(true);
      const ogeler = [
        ...selectedDaireIDs.map(id => ({ ogeTuru: 'Daire', gayrimenkulID: id })),
        ...selectedPortfoyIDs.map(id => ({ ogeTuru: 'Portfoy', gayrimenkulID: id })),
      ];
      const res = await axiosInstance.post(`/api/MusteriSunumlari/${sunum.sunumID}/ogeler`, ogeler);
      toast.success(res.data?.message || 'Öğeler eklendi.');
      setShowAddDaireModal(false);
      fetchSunum();
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setAddingDaireler(false);
    }
  };

  /**
   * Akıllı Arama: yapay zekaya YALNIZCA kullanıcının cümlesi gider.
   * AI pasif/hatalı ise sessizce yerel Türkçe ayrıştırıcıya düşer.
   */
  const handleAkilliAra = async () => {
    const metin = akilliMetin.trim();
    if (metin.length < 3) return;
    setAkilliLoading(true);
    setAkilliHata(null);

    let kriterler: AramaKriterleri;
    let kaynak: 'ai' | 'yerel' = 'yerel';
    try {
      const res = await axiosInstance.post('/api/AkilliArama/kriter-cikar', { metin }, { timeout: 25000 });
      if (res.data?.basarili && res.data?.kriterler) {
        const r = res.data.kriterler;
        kriterler = {
          ...bosKriterler(),
          odaTipleri: r.odaTipleri || [],
          minFiyat: r.minFiyat ?? null,
          maxFiyat: r.maxFiyat ?? null,
          minM2: r.minM2 ?? null,
          maxM2: r.maxM2 ?? null,
          m2Turu: r.m2Turu ?? null,
          yeniProje: r.yeniProje ?? null,
          sehir: r.sehir ?? null,
          katTercihi: r.katTercihi ?? null,
          cephe: r.cephe ?? null,
          manzara: r.manzara ?? null,
          islemTuru: r.islemTuru ?? null,
          gayrimenkulTuru: r.gayrimenkulTuru ?? null,
          serbestAnahtarKelimeler: r.serbestAnahtarKelimeler || [],
        };
        kaynak = 'ai';
      } else {
        kriterler = parseAramaKriterleri(metin);
      }
    } catch {
      kriterler = parseAramaKriterleri(metin);
    }

    const projeAdlari = tumDaireler.map(d => d.proje?.projeAdi).filter((p): p is string => !!p);
    const zengin = zenginlestirKriterler(kriterler, metin, Array.from(new Set(projeAdlari)));

    if (kriterEtiketleri(zengin).length === 0) {
      setAkilliHata('Cümleden bir kriter çıkarılamadı. Örn: "5-7 milyon aralığında 2+1 daire".');
    }
    setAkilliKriterler(zengin);
    setAkilliKaynak(kaynak);
    setAkilliLoading(false);
  };

  const handleKriterSil = (alan: KriterEtiketi['alan']) => {
    setAkilliKriterler(prev => {
      if (!prev) return prev;
      if (alan === 'fiyat') return { ...prev, minFiyat: null, maxFiyat: null };
      if (alan === 'm2') return { ...prev, minM2: null, maxM2: null, m2Turu: null };
      if (alan === 'odaTipleri') return { ...prev, odaTipleri: [] };
      return { ...prev, [alan]: null } as AramaKriterleri;
    });
  };

  const roundInt = (n: number) => Math.round(n);

  // Kilitli olmayan kalemleri hedefTutar'a ulaşacak şekilde yeniden dağıtır.
  // Kilitli kalemlerin tutarı sabit kalır; yuvarlamadan kalan fark en sondaki kilitsiz kaleme eklenir.
  const redistributeKalemler = (kalemler: OdemeKalem[], hedefTutar: number): OdemeKalem[] => {
    const kilitliToplam = kalemler.filter(k => k.kilitli).reduce((s, k) => s + (parseFloat(k.miktar) || 0), 0);
    const kilitsizIdx = kalemler.map((k, i) => (k.kilitli ? -1 : i)).filter(i => i >= 0);
    if (kilitsizIdx.length === 0) return kalemler;

    const kalan = Math.max(0, hedefTutar - kilitliToplam);
    const kilitsizOncekiToplam = kilitsizIdx.reduce((s, i) => s + (parseFloat(kalemler[i].miktar) || 0), 0);

    const yeni = [...kalemler];
    let dagitilan = 0;
    kilitsizIdx.forEach((idx, pos) => {
      const sonKalem = pos === kilitsizIdx.length - 1;
      let pay: number;
      if (sonKalem) {
        pay = kalan - dagitilan;
      } else {
        const oran = kilitsizOncekiToplam > 0
          ? (parseFloat(kalemler[idx].miktar) || 0) / kilitsizOncekiToplam
          : 1 / kilitsizIdx.length;
        pay = roundInt(kalan * oran);
        dagitilan += pay;
      }
      yeni[idx] = { ...yeni[idx], miktar: String(Math.max(0, pay)) };
    });
    return yeni;
  };

  // Ortalama vade (gün): sözleşme tarihi henüz yok, bugünü baz alarak tutar-ağırlıklı hesaplanır.
  const calculateOrtalamaVadeGun = (kalemler: OdemeKalem[]): number => {
    if (kalemler.length === 0) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let toplamAgirlikliGun = 0;
    let toplamTutar = 0;
    kalemler.forEach(k => {
      if (!k.vadeTarihi) return;
      const vade = new Date(k.vadeTarihi);
      const gun = Math.max(0, Math.round((vade.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
      const miktar = parseFloat(k.miktar) || 0;
      toplamAgirlikliGun += gun * miktar;
      toplamTutar += miktar;
    });
    return toplamTutar > 0 ? Math.round(toplamAgirlikliGun / toplamTutar) : 0;
  };

  // Vade bazlı fiyat önerisini, kilitli olmayan kalemler başlangıçtaki oranlarına göre
  // yeniden dağıtıldığında OLUŞACAK ortalama vadeyi dikkate alarak hesaplar. Tutar arttıkça
  // vade de değişebildiğinden (kilitli kalemler nedeniyle), sabit noktaya ulaşana kadar
  // birkaç iterasyonla çözülür — böylece kullanıcı "Teklife Uygula"ya birden fazla kez
  // basmak zorunda kalmadan tek seferde doğru/nihai değeri görür.
  const hesaplaVadeBazliOneri = (
    kalemler: OdemeKalem[],
    listeFiyati: number,
    ayar: { aktifMi: boolean; aylikArtisOrani: number; kabulEdilenOrtalamaVadeGun: number } | null
  ): { onerilenFiyat: number | null; vadeFarkGun: number } => {
    if (!ayar?.aktifMi || listeFiyati <= 0 || kalemler.length === 0) {
      return { onerilenFiyat: null, vadeFarkGun: 0 };
    }

    let p = listeFiyati;
    let vade = calculateOrtalamaVadeGun(kalemler);
    for (let i = 0; i < 10; i++) {
      vade = calculateOrtalamaVadeGun(redistributeKalemler(kalemler, p));
      const fark = Math.max(0, vade - ayar.kabulEdilenOrtalamaVadeGun);
      const yeniP = Math.round(listeFiyati + listeFiyati * (ayar.aylikArtisOrani / 100) * (fark / 30));
      if (Math.abs(yeniP - p) < 1) { p = yeniP; break; }
      p = yeniP;
    }

    const nihaiFark = vade - ayar.kabulEdilenOrtalamaVadeGun;
    if (nihaiFark <= 0) return { onerilenFiyat: null, vadeFarkGun: 0 };
    return { onerilenFiyat: p, vadeFarkGun: nihaiFark };
  };

  // Ödeme planı şablonundan kalem listesi üretir (bugünü baz alarak; peşinat + ara ödemeler + eşit taksitler).
  const generateSablonKalemleri = (sablon: OdemePlanSablonu, miktar: number): OdemeKalem[] => {
    const today = new Date();
    const addMonths = (d: Date, m: number) => { const r = new Date(d); r.setMonth(r.getMonth() + m); return r; };
    const toIso = (d: Date) => d.toISOString().slice(0, 10);

    const rows: { vadeTarihi: string; miktar: number; aciklama: string; ayEndeksi: number }[] = [];

    const pesinatMiktar = sablon.pesinatTipi === 'Oran'
      ? roundInt(miktar * sablon.pesinatDegeri / 100)
      : Math.min(roundInt(sablon.pesinatDegeri), roundInt(miktar));
    if (pesinatMiktar > 0) {
      rows.push({ vadeTarihi: toIso(today), miktar: pesinatMiktar, aciklama: 'Peşinat', ayEndeksi: 0 });
    }

    let kalan = miktar - pesinatMiktar;
    const detaylar = [...sablon.detaylar].sort((a, b) => a.vadeAyEndeksi - b.vadeAyEndeksi);
    const detayAySet = new Set<number>();
    detaylar.forEach(d => {
      let tutar = 0;
      if (d.hesaplamaYontemi === 'ToplaminYuzdesi') tutar = roundInt(miktar * d.deger / 100);
      else if (d.hesaplamaYontemi === 'KalaninYuzdesi') tutar = roundInt(kalan * d.deger / 100);
      else tutar = roundInt(d.deger);
      kalan -= tutar;
      detayAySet.add(d.vadeAyEndeksi);
      rows.push({ vadeTarihi: toIso(addMonths(today, d.vadeAyEndeksi)), miktar: tutar, aciklama: d.odemeTuru, ayEndeksi: d.vadeAyEndeksi });
    });

    const n = sablon.vadeTipi === 'SerbestOdeme' ? 0 : Math.max(0, sablon.maksimumTaksitSayisi - detaylar.length);
    if (n > 0 && kalan > 0) {
      const ayIndeksleri: number[] = [];
      let ay = 1;
      while (ayIndeksleri.length < n) {
        if (!detayAySet.has(ay)) ayIndeksleri.push(ay);
        ay++;
      }
      const taksitTutari = Math.floor(kalan / n);
      ayIndeksleri.forEach((ayIdx, i) => {
        // Yuvarlamadan kalan fark en sondaki taksite eklenir.
        const tutar = i < n - 1 ? taksitTutari : kalan - taksitTutari * (n - 1);
        rows.push({ vadeTarihi: toIso(addMonths(today, ayIdx)), miktar: tutar, aciklama: `${i + 1}. Taksit`, ayEndeksi: ayIdx });
      });
    }

    rows.sort((a, b) => a.ayEndeksi - b.ayEndeksi);
    return rows.map(r => ({ kalemID: null, vadeTarihi: r.vadeTarihi, miktar: String(r.miktar), aciklama: r.aciklama, kilitli: false }));
  };

  const updateAktifTeklif = (updater: (t: TeklifTab) => TeklifTab) => {
    setOdemePlaniTeklifler(prev => prev.map((t, i) => i === aktifTeklifTab ? updater(t) : t));
  };

  // Proje fiyatlandırma kontrolü — teklif ortalama vadesi bugünü baz alır (calculateOrtalamaVadeGun ile aynı)
  const teklifFiyatKontrol = (teklif: TeklifTab) => kontrolEt({
    kural: fiyatKurali,
    listeFiyati: showOdemePlaniModal?.listeFiyati,
    satisFiyati: parseFloat((teklif.teklifMiktari || '0').replace(/[^\d.]/g, '')) || 0,
    ortalamaVade: calculateOrtalamaVadeGun(teklif.kalemler),
    vadeTarihleri: teklif.kalemler.map(k => k.vadeTarihi),
    kullaniciID: user?.kullaniciID,
  });

  const tarihGirilebilir = (...tarihler: string[]) => {
    if (tarihler.every(tarih => tarihIzinliMi(fiyatKurali, tarih, user?.kullaniciID))) return true;
    toast.error(sonOdemeTarihiMesaji(fiyatKurali!));
    return false;
  };

  const portfoyFotoUrl = (dosyaYolu: string) => `${API_BASE_URL}/api/EmlakPortfoy/foto/${dosyaYolu}`;

  const openPortfoyDetay = async (portfoyID: number) => {
    setPortfoyDetayLoading(true);
    setPortfoyAktifFoto(0);
    try {
      const res = await axiosInstance.get(`/api/EmlakPortfoy/${portfoyID}`);
      setPortfoyDetay(res.data as PortfoyDetayData);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setPortfoyDetayLoading(false);
    }
  };

  const openOdemePlaniModal = async (sd: SunumDaireDetay) => {
    if (!sunum) return;
    const gerceklesenFiyat = sd.sunumOgeTuru === 'Portfoy' ? (sd.portfoy?.fiyat ?? null) : (sd.daire?.listeFiyati ?? null);
    const baslik = sd.sunumOgeTuru === 'Portfoy' ? (sd.portfoy?.baslik || String(sd.gayrimenkulID)) : (sd.daire?.daireNo ? `Daire ${sd.daire.daireNo}` : String(sd.gayrimenkulID));
    const projeID = sd.sunumOgeTuru === 'Portfoy' ? null : (sd.daire?.projeID ?? null);
    setShowOdemePlaniModal({ sunumDaireID: sd.sunumDaireID, gayrimenkulID: sd.gayrimenkulID, baslik, listeFiyati: gerceklesenFiyat, ogeTuru: sd.sunumOgeTuru, projeID });
    setFiyatKurali(null);
    setShowFiyatAraliklari(false);
    setOdemePlaniLoading(true);
    setAktifTeklifTab(0);
    setTopluTaksitSayisi('12');
    setTopluIlkTarih('');
    setTopluAciklama('');
    setTopluPlanTutari('');
    setTopluOdemeTuru('Nakit');
    setSeciliSablonId('');
    setSablonOdemeTuru('Nakit');
    setYeniKalem({ vadeTarihi: '', miktar: '', tur: 'Taksit', odemeTuru: 'Nakit', aciklama: '' });
    try {
      const [teklifRes, sablonRes, fiyatlamaRes, kuralRes] = await Promise.all([
        axiosInstance.get('/api/SunumGeciTeklifler/liste', { params: { sunumTuru: 'Musteri', sunumID: sunum.sunumID, gayrimenkulID: sd.gayrimenkulID } }),
        axiosInstance.get('/api/OdemePlanSablonlari').catch(() => ({ data: [] })),
        axiosInstance.get('/api/GayrimenkulFiyatlamaAyarlari').catch(() => ({ data: null })),
        projeID ? axiosInstance.get(`/api/ProjeFiyatlandirma/${projeID}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setFiyatKurali(kuralRes.data);
      setOdemePlanSablonlari((sablonRes.data || []).filter((s: OdemePlanSablonu) => s.isActive));
      setFiyatlamaAyari(fiyatlamaRes.data ? {
        aktifMi: !!fiyatlamaRes.data.aktifMi,
        aylikArtisOrani: fiyatlamaRes.data.aylikArtisOrani ?? 0,
        kabulEdilenOrtalamaVadeGun: fiyatlamaRes.data.kabulEdilenOrtalamaVadeGun ?? 0,
      } : null);
      const liste: any[] = teklifRes.data || [];
      if (liste.length > 0) {
        setOdemePlaniTeklifler(liste.map(r => ({
          teklifID: r.teklifID,
          teklifAdi: r.teklifAdi,
          siraNo: r.siraNo,
          teklifMiktari: r.teklifMiktari != null ? String(r.teklifMiktari) : '',
          kalemler: r.kalemler.map((k: any) => ({ kalemID: k.kalemID, vadeTarihi: k.vadeTarihi, miktar: String(k.miktar), aciklama: k.aciklama || '', odemeTuru: k.odemeTuru || 'Nakit' })),
        })));
      } else {
        const teklifMiktar = gerceklesenFiyat ? String(gerceklesenFiyat) : '';
        setOdemePlaniTeklifler([{ teklifID: null, teklifAdi: 'Teklif 1', siraNo: 1, teklifMiktari: teklifMiktar, kalemler: [] }]);
      }
    } catch {
      toast.error(t('msg.islemHatasi'));
      setShowOdemePlaniModal(null);
    } finally {
      setOdemePlaniLoading(false);
    }
  };

  const handleTeklifEkle = () => {
    setOdemePlaniTeklifler(prev => {
      const yeni: TeklifTab = {
        teklifID: null,
        teklifAdi: `Teklif ${prev.length + 1}`,
        siraNo: prev.length + 1,
        teklifMiktari: showOdemePlaniModal?.listeFiyati ? String(showOdemePlaniModal.listeFiyati) : '',
        kalemler: [],
      };
      const next = [...prev, yeni];
      setAktifTeklifTab(next.length - 1);
      return next;
    });
  };

  const handleTeklifSil = async (index: number) => {
    if (odemePlaniTeklifler.length <= 1) return;
    const teklif = odemePlaniTeklifler[index];
    if (teklif.teklifID) {
      try {
        await axiosInstance.delete(`/api/SunumGeciTeklifler/${teklif.teklifID}`);
      } catch {
        toast.error(t('msg.islemHatasi'));
        return;
      }
    }
    setOdemePlaniTeklifler(prev => prev.filter((_, i) => i !== index));
    setAktifTeklifTab(prev => (prev >= index ? Math.max(0, prev - 1) : prev));
  };

  const saveTeklif = async (teklif: TeklifTab, siraNo: number): Promise<number> => {
    const teklifMiktarNum = teklif.teklifMiktari ? parseFloat(teklif.teklifMiktari.replace(/[^\d.]/g, '')) || null : null;
    const ortalamaVadeGun = calculateOrtalamaVadeGun(teklif.kalemler) || null;
    const upsertRes = await axiosInstance.put('/api/SunumGeciTeklifler', {
      teklifID: teklif.teklifID,
      sunumTuru: 'Musteri',
      sunumID: sunum!.sunumID,
      gayrimenkulID: showOdemePlaniModal!.gayrimenkulID,
      teklifAdi: teklif.teklifAdi,
      siraNo,
      teklifMiktari: teklifMiktarNum,
      ortalamaVadeGun,
    });
    const teklifID = upsertRes.data.teklifID;
    const kalemlerPayload = teklif.kalemler.map((k, idx) => ({ vadeTarihi: k.vadeTarihi, miktar: parseFloat(k.miktar || '0'), aciklama: k.aciklama || null, odemeTuru: k.odemeTuru || 'Nakit', sira: idx }));
    await axiosInstance.post(`/api/SunumGeciTeklifler/${teklifID}/kalemler/toplu`, kalemlerPayload);
    return teklifID;
  };

  const handleOdemePlaniSave = async () => {
    if (!sunum || !showOdemePlaniModal || odemePlaniTeklifler.length === 0) return;
    setOdemePlaniSaving(true);
    try {
      const kaydedilenIDler: number[] = [];
      for (let i = 0; i < odemePlaniTeklifler.length; i++) {
        kaydedilenIDler.push(await saveTeklif(odemePlaniTeklifler[i], i + 1));
      }
      // Modal açık kalıyor: yeni teklifler tekrar kaydedilince çoğalmasın diye ID'leri state'e yaz
      setOdemePlaniTeklifler(prev => prev.map((t, i) => ({ ...t, teklifID: kaydedilenIDler[i] ?? t.teklifID, siraNo: i + 1 })));
      toast.success('Kaydedildi');
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setOdemePlaniSaving(false);
    }
  };

  // "Teklif Olarak Yazdır": önce belgeye dahil edilecek bilgiler sorulur (seçim tarayıcıda hatırlanır)
  const handleTeklifYazdirAc = () => {
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!aktif) return;
    if (teklifFiyatKontrol(aktif).engelli) {
      toast.error('Ödeme planı proje fiyatlandırma kurallarına uymadığı için teklif yazdırılamaz.');
      return;
    }
    setTeklifYazdirModal(true);
  };

  const handleTeklifYazdir = async () => {
    if (!sunum || !showOdemePlaniModal) return;
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!aktif) return;
    if (teklifFiyatKontrol(aktif).engelli) {
      toast.error('Ödeme planı proje fiyatlandırma kurallarına uymadığı için teklif yazdırılamaz.');
      return;
    }
    teklifYazdirSecenekYaz(teklifYazdirSecenek);
    setTeklifYazdirModal(false);
    setTeklifYazdiriliyor(true);
    try {
      const teklifID = await saveTeklif(aktif, aktifTeklifTab + 1);
      setOdemePlaniTeklifler(prev => prev.map((t, i) => i === aktifTeklifTab ? { ...t, teklifID } : t));
      const res = await axiosInstance.get(`/api/SunumGeciTeklifler/${teklifID}/pdf`, {
        responseType: 'blob',
        params: { listeFiyati: teklifYazdirSecenek.listeFiyati, ortalamaVade: teklifYazdirSecenek.ortalamaVade },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setTeklifYazdiriliyor(false);
    }
  };

  const handleSozlesmeYap = async () => {
    if (!sunum || !showOdemePlaniModal) return;
    const sd = sunum.daireler.find(d => d.sunumDaireID === showOdemePlaniModal.sunumDaireID);
    if (!sd?.daire || sd.daire.daireDurumu !== 'Satışta') {
      toast.error('Bu daire artık "Satışta" durumunda değil, sözleşme oluşturulamaz.');
      return;
    }
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!aktif || !aktif.teklifMiktari || aktif.kalemler.length === 0) {
      toast.error('Sözleşme oluşturmadan önce teklif miktarı ve ödeme planı kalemleri girilmelidir.');
      return;
    }
    setSozlesmeYapiliyor(true);
    try {
      const teklifID = await saveTeklif(aktif, aktifTeklifTab + 1);
      setOdemePlaniTeklifler(prev => prev.map((t, i) => i === aktifTeklifTab ? { ...t, teklifID } : t));
      navigate(`/musteri-sunumlari/${sunum.sunumID}/teklif-sozlesme/${teklifID}`);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setSozlesmeYapiliyor(false);
    }
  };

  const handleTopluPlanOlustur = () => {
    if (!topluIlkTarih || !topluTaksitSayisi || !topluPlanTutari) return;
    const sayisi = parseInt(topluTaksitSayisi);
    const planTutari = parseFloat(topluPlanTutari.replace(/[^\d.]/g, ''));
    if (!sayisi || !planTutari) return;
    const taksitMiktari = Math.floor(planTutari / sayisi);
    const yeniKalemler: OdemeKalem[] = [];
    const ilkTarih = new Date(topluIlkTarih);
    const sonVade = new Date(ilkTarih);
    sonVade.setMonth(sonVade.getMonth() + sayisi - 1);
    if (!tarihGirilebilir(sonVade.toISOString().slice(0, 10))) return;
    for (let i = 0; i < sayisi; i++) {
      const vade = new Date(ilkTarih);
      vade.setMonth(vade.getMonth() + i);
      yeniKalemler.push({ kalemID: null, vadeTarihi: vade.toISOString().slice(0, 10), miktar: i < sayisi - 1 ? String(taksitMiktari) : String(planTutari - taksitMiktari * (sayisi - 1)), aciklama: topluAciklama ? `${topluAciklama} ${i + 1}` : `${i + 1}. Taksit`, odemeTuru: topluOdemeTuru });
    }
    updateAktifTeklif(t => ({ ...t, kalemler: yeniKalemler }));
    toast.success(t('msg.islemBasarili'));
  };

  const handleKalemEkle = () => {
    const miktar = parseFloat(yeniKalem.miktar) || 0;
    if (!yeniKalem.vadeTarihi || miktar <= 0) {
      toast.error('Vade tarihi ve miktar girilmelidir.');
      return;
    }
    if (!tarihGirilebilir(yeniKalem.vadeTarihi)) return;
    const aciklama = yeniKalem.tur === 'Pesinat' ? 'Peşinat'
      : yeniKalem.tur === 'Kapora' ? 'Kapora'
      : yeniKalem.aciklama.trim();
    updateAktifTeklif(t => {
      // Elle girilen tutar kilitlenir; kalan tutar diğer kalemlere dağıtılır (satırda tutar düzenlemekle aynı davranış)
      const ks = planiDuzenle([...t.kalemler, {
        kalemID: null, vadeTarihi: yeniKalem.vadeTarihi, miktar: String(miktar), aciklama, odemeTuru: yeniKalem.odemeTuru, kilitli: true,
      }]);
      const hedef = parseFloat(t.teklifMiktari.replace(/[^\d.]/g, '')) || 0;
      return { ...t, kalemler: hedef > 0 ? redistributeKalemler(ks, hedef) : ks };
    });
    setYeniKalem(prev => ({ ...prev, vadeTarihi: '', miktar: '', tur: 'Taksit', aciklama: '' }));
  };

  const updateKalem = (index: number, degisiklik: Partial<OdemeKalem>) => {
    updateAktifTeklif(t => {
      const ks = [...t.kalemler];
      ks[index] = { ...ks[index], ...degisiklik };
      return { ...t, kalemler: ks };
    });
  };

  const handleSablonUygula = () => {
    const sablon = odemePlanSablonlari.find(s => s.sablonId === seciliSablonId);
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!sablon || !aktif) return;
    const miktar = parseFloat(aktif.teklifMiktari.replace(/[^\d.]/g, '')) || 0;
    if (!miktar) {
      toast.error('Önce teklif miktarı girin.');
      return;
    }

    // 1. Önce mevcut tutara göre bir taslak plan oluşturup ortalama vadeyi hesapla
    const taslakKalemler = generateSablonKalemleri(sablon, miktar);
    if (!tarihGirilebilir(...taslakKalemler.map(k => k.vadeTarihi))) return;
    const taslakVade = calculateOrtalamaVadeGun(taslakKalemler);

    // 2. Bu vadeye göre olması gereken (vade bazlı artışlı) teklif tutarını hesapla.
    //    Projenin kendi fiyatlandırma kontrolü açıksa genel öneri devreye girmez.
    const listeFiyati = showOdemePlaniModal?.listeFiyati || 0;
    let hedefMiktar = miktar;
    if (!kuralAktifMi(fiyatKurali) && fiyatlamaAyari?.aktifMi && listeFiyati > 0 && taslakVade > fiyatlamaAyari.kabulEdilenOrtalamaVadeGun) {
      const vadeFarkGun = taslakVade - fiyatlamaAyari.kabulEdilenOrtalamaVadeGun;
      const ekAy = vadeFarkGun / 30;
      const artisTutari = listeFiyati * (fiyatlamaAyari.aylikArtisOrani / 100) * ekAy;
      const onerilenMiktar = Math.round(listeFiyati + artisTutari);
      if (onerilenMiktar > miktar) {
        const onay = window.confirm(
          `Bu şablonun ortalama vadesi ${taslakVade} gün, kabul edilen vadeyi (${fiyatlamaAyari.kabulEdilenOrtalamaVadeGun} gün) ${vadeFarkGun} gün aşıyor.\n\n` +
          `Teklif tutarı ${onerilenMiktar.toLocaleString('tr-TR')} ₺ olarak güncellenip ödeme planı bu tutara göre mi oluşturulsun?`
        );
        if (onay) hedefMiktar = onerilenMiktar;
      }
    }

    // 3. Nihai (varsa artışlı) tutara göre planı oluştur
    const kalemler = (hedefMiktar === miktar ? taslakKalemler : generateSablonKalemleri(sablon, hedefMiktar))
      .map(k => ({ ...k, odemeTuru: sablonOdemeTuru }));
    updateAktifTeklif(t => ({ ...t, teklifMiktari: String(hedefMiktar), kalemler }));
    toast.success(t('msg.islemBasarili'));
  };

  // Teklif miktarından kilitli kalemlerin toplamı düşülür; kalan tutar kilitsiz kalemlere eşit
  // bölünür, yuvarlamadan kalan fark son kilitsiz kaleme eklenir.
  const handleEsitDagit = () => {
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!aktif || aktif.kalemler.length === 0) return;
    const hedef = parseFloat(aktif.teklifMiktari.replace(/[^\d.]/g, '')) || 0;
    if (!hedef) { toast.error('Önce teklif miktarı girin.'); return; }
    const kilitsizIdx = aktif.kalemler.map((k, i) => (k.kilitli ? -1 : i)).filter(i => i >= 0);
    if (kilitsizIdx.length === 0) { toast.error('Dağıtılacak kilitsiz kalem yok.'); return; }
    const kilitliToplam = aktif.kalemler.filter(k => k.kilitli).reduce((s, k) => s + (parseFloat(k.miktar) || 0), 0);
    const kalan = hedef - kilitliToplam;
    if (kalan <= 0) { toast.error('Kilitli kalemlerin toplamı teklif miktarına ulaşıyor; dağıtılacak tutar kalmadı.'); return; }
    const pay = Math.floor(kalan / kilitsizIdx.length);
    const sonIdx = kilitsizIdx[kilitsizIdx.length - 1];
    updateAktifTeklif(t => ({
      ...t,
      kalemler: t.kalemler.map((k, i) => k.kilitli ? k
        : { ...k, miktar: String(i === sonIdx ? kalan - pay * (kilitsizIdx.length - 1) : pay) }),
    }));
  };

  const handleKalemleriTemizle = () => {
    const aktif = odemePlaniTeklifler[aktifTeklifTab];
    if (!aktif || aktif.kalemler.length === 0) return;
    if (!window.confirm('Bu teklifin tüm ödeme kalemleri silinsin mi?')) return;
    updateAktifTeklif(t => ({ ...t, kalemler: [] }));
  };

  const fetchLinkler = async () => {
    if (!sunum) return;
    setLinklerLoading(true);
    try {
      const res = await axiosInstance.get(`/api/SunumMusteriLinks/Musteri/${sunum.sunumID}`);
      setLinkler(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLinklerLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!sunum) return;
    setCreatingLink(true);
    try {
      await axiosInstance.post('/api/SunumMusteriLinks', { 
        sunumTuru: 'Musteri', 
        sunumID: sunum.sunumID, 
        gunSayisi: parseInt(yeniLinkGun) || 3,
        frontendUrl: window.location.origin
      });
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setCreatingLink(false);
    }
  };

  const handleExtendLink = async (linkID: number) => {
    try {
      await axiosInstance.put(`/api/SunumMusteriLinks/${linkID}/uzat`, { ekGunSayisi: 3 });
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDeactivateLink = async (linkID: number) => {
    try {
      await axiosInstance.delete(`/api/SunumMusteriLinks/${linkID}`);
      toast.success(t('msg.islemBasarili'));
      fetchLinkler();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleBaslatGorusme = async () => {
    if (!sunum) return;
    setGorusmeLoading(true);
    try {
      const res = await axiosInstance.post('/api/OnlineGorusmeler/baslat', { sunumID: sunum.sunumID });
      setActiveGorusme({ gorusmeID: res.data.gorusmeID, odaAdi: res.data.odaAdi, baslamaTarihi: res.data.baslamaTarihi });
      toast.success(t('msg.islemBasarili'));
      const w = window.screen.width;
      const h = window.screen.height;
      window.open(
        res.data.jitsiUrl,
        'JitsiGorusme',
        `toolbar=no,menubar=no,location=no,status=no,scrollbars=no,resizable=yes,width=${Math.round(w * 0.85)},height=${Math.round(h * 0.85)},left=${Math.round(w * 0.075)},top=${Math.round(h * 0.075)}`
      );
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setGorusmeLoading(false);
    }
  };

  const handleBitirGorusme = async () => {
    if (!activeGorusme) return;
    setGorusmeLoading(true);
    try {
      await axiosInstance.patch(`/api/OnlineGorusmeler/${activeGorusme.gorusmeID}/bitir`);
      setActiveGorusme(null);
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setGorusmeLoading(false);
    }
  };

  const handleCopyLink = (linkID: number, url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkId(linkID);
      setTimeout(() => setCopiedLinkId(null), 2000);
    });
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatFiyat = (f: number | null) => f ? f.toLocaleString('tr-TR') + ' ₺' : '—';

  // Daire ekleme filtre türetme
  const addProjeler = Array.from(
    new Map(tumDaireler.filter(d => d.proje).map(d => [d.proje!.projeID, d.proje!])).values()
  );
  const projedeki = (d: DaireItem) => !addFilterProjeID || d.proje?.projeID.toString() === addFilterProjeID;
  const addBloklar = Array.from(
    new Map(tumDaireler.filter(d => d.blok && projedeki(d)).map(d => [d.blok!.blokID, d.blok!])).values()
  ).sort((a, b) => a.blokAdi.localeCompare(b.blokAdi, 'tr'));
  const addKatlar = Array.from(
    new Map(
      tumDaireler
        .filter(d => d.kat && projedeki(d) && (!addFilterBlokID || d.blok?.blokID.toString() === addFilterBlokID))
        .map(d => [d.kat!.katID, d.kat!])
    ).values()
  );
  const addManzaralar = Array.from(
    new Map(tumDaireler.filter(d => d.manzara && projedeki(d)).map(d => [d.manzara!.manzaraID, d.manzara!])).values()
  ).sort((a, b) => a.manzaraAdi.localeCompare(b.manzaraAdi, 'tr'));
  const addTipler = Array.from(
    new Map(tumDaireler.filter(d => d.daireTipi && projedeki(d)).map(d => [d.daireTipi!.daireTipID, d.daireTipi!])).values()
  ).sort((a, b) => a.tip.localeCompare(b.tip, 'tr', { numeric: true }));
  const addCepheler = Array.from(
    new Map(
      tumDaireler
        .filter(d => d.cephe && (!addFilterProjeID || d.proje?.projeID.toString() === addFilterProjeID))
        .map(d => [d.cephe!.cepheID, d.cephe!])
    ).values()
  );
  const mevcutDaireIDs = new Set(sunum?.daireler.filter(d => d.sunumOgeTuru === 'Daire').map(d => d.daireID) ?? []);
  const filteredAddDaireler = tumDaireler.filter(d => {
    if (mevcutDaireIDs.has(d.daireID)) return false;
    if (addFilterProjeID && d.proje?.projeID.toString() !== addFilterProjeID) return false;
    if (addFilterKatID && d.kat?.katID.toString() !== addFilterKatID) return false;
    if (addFilterCepheID && d.cephe?.cepheID.toString() !== addFilterCepheID) return false;
    if (addFilterBlokID && d.blok?.blokID.toString() !== addFilterBlokID) return false;
    if (addFilterManzaraID && d.manzara?.manzaraID.toString() !== addFilterManzaraID) return false;
    if (addFilterTipID && d.daireTipi?.daireTipID.toString() !== addFilterTipID) return false;
    if (addDaireSearch) {
      const s = addDaireSearch.toLowerCase();
      return (
        d.daireNo.toLowerCase().includes(s) ||
        (d.proje?.projeAdi.toLowerCase().includes(s) ?? false) ||
        (d.kat?.katAdi.toLowerCase().includes(s) ?? false)
      );
    }
    return true;
  });

  const mevcutPortfoyIDs = new Set(sunum?.daireler.filter(d => d.sunumOgeTuru === 'Portfoy').map(d => d.portfoyID) ?? []);

  // Akıllı Arama sonuçları — tamamen istemcide, kriter çipi silinince ağ isteği olmadan yeniden hesaplanır
  const akilliSonuclar: AramaSonucu[] = React.useMemo(
    () => (akilliKriterler
      ? eslestir(akilliKriterler, tumDaireler, tumPortfoyler, { mevcutDaireIDs, mevcutPortfoyIDs })
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [akilliKriterler, tumDaireler, tumPortfoyler, sunum]
  );
  const filteredAddPortfoyler = tumPortfoyler.filter(p => {
    if (mevcutPortfoyIDs.has(p.portfoyID)) return false;
    if (addPortfoySearch) {
      const s = addPortfoySearch.toLowerCase();
      return (
        p.baslik.toLowerCase().includes(s) ||
        (p.adres?.toLowerCase().includes(s) ?? false) ||
        p.ilanNo.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const selSt: React.CSSProperties = {
    padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 7,
    fontSize: 12.5, background: '#fff', color: '#374151', outline: 'none',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: '#64748b' }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
      </div>
    );
  }

  if (!sunum) {
    return <div style={{ padding: '2rem', color: '#ef4444' }}>Sunum bulunamadı.</div>;
  }

  const badge = DURUM_BADGE[sunum.durum] || { bg: '#f1f5f9', color: '#475569' };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Geri */}
      <button onClick={() => navigate('/musteri-sunumlari')}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13.5, marginBottom: 20, padding: 0 }}
        onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
        onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        {t('menu.sunumTeklif', 'Sunum & Teklif')}
      </button>

      {/* Sunum Başlık Kartı */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: '3px solid #1a2440' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {sunum.baslik || (sunum.cari?.adiSoyadi + ' Sunumu')}
              </h1>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 9999, background: badge.bg, color: badge.color }}>
                {sunum.durum}
              </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px' }}>
              {sunum.cari && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{sunum.cari.adiSoyadi}</div>
                  {sunum.cari.firmaAdi && <div style={{ fontSize: 12.5, color: '#64748b' }}>{sunum.cari.firmaAdi}</div>}
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>{sunum.cari.telefon1}</div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Takip No</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{sunum.takipNo}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Oluşturma</span>
                <span style={{ fontSize: 13, color: '#374151' }}>{formatDate(sunum.olusturmaTarihi)}</span>
              </div>
              {sunum.olusturanAdSoyad && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Oluşturan</span>
                  <span style={{ fontSize: 13, color: '#374151' }}>{sunum.olusturanAdSoyad}</span>
                </div>
              )}
            </div>
          </div>

          {/* Durum Değiştir */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Sunum Durumu</label>
            <select
              value={sunum.durum}
              disabled={savingDurum}
              onChange={e => handleSunumDurumChange(e.target.value)}
              style={{ ...selSt, minWidth: 180, fontSize: 13, padding: '8px 12px' }}
            >
              {DURUM_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Özet */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Toplam Daire', value: sunum.daireler.length, color: '#1a2440' },
          {
            label: 'Olumlu Tepki',
            value: sunum.daireler.filter(d => ['Beğendi', 'Almak İstiyor', 'İlgileniyor'].includes(d.musteriTepkisi)).length,
            color: '#059669',
          },
          {
            label: 'Olumsuz Tepki',
            value: sunum.daireler.filter(d => ['Fiyat Yüksek', 'Uygun Değil'].includes(d.musteriTepkisi)).length,
            color: '#ef4444',
          },
        ].map(item => (
          <div key={item.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: item.color }}>{item.value}</div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Daireler */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#fff', margin: 0 }}>
            Sunulan Daireler
            <span style={{ marginLeft: 8, fontSize: 12, background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '2px 8px', borderRadius: 9999 }}>
              {sunum.daireler.length}
            </span>
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {activeGorusme ? (
              <button
                onClick={handleBitirGorusme}
                disabled={gorusmeLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
                  opacity: gorusmeLoading ? 0.7 : 1,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#ef4444')}
                onMouseLeave={e => (e.currentTarget.style.background = '#dc2626')}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                Görüşmeyi Bitir
              </button>
            ) : (
              <>
              <button
                onClick={openDokumanModal}
                title="Döküman Paylaş"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
                  padding: '7px 11px', fontSize: 14, cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
              >
                <i className="fas fa-file-export" />
              </button>
              <button
                onClick={handleBaslatGorusme}
                disabled={gorusmeLoading}
                title="Online Görüşme Başlat"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
                  padding: '7px 11px', fontSize: 14, cursor: 'pointer', transition: 'background 0.15s',
                  opacity: gorusmeLoading ? 0.7 : 1,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
              >
                <i className="fas fa-video" />
              </button>
              </>
            )}
            <button
              onClick={() => { setShowLinkModal(true); fetchLinkler(); }}
              title="Müşteri Linki"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
                padding: '7px 11px', fontSize: 14, cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
            >
              <i className="fas fa-link" />
            </button>
            <button
              onClick={() => openAddDaireModal('Akilli')}
              title="Akıllı Arama ile ekle"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8,
                padding: '7px 11px', fontSize: 14, cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#8b5cf6')}
              onMouseLeave={e => (e.currentTarget.style.background = '#7c3aed')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                <path d="M18 15l.8 2L21 17.8l-2.2.8L18 21l-.8-2.4-2.2-.8 2.2-.8L18 15z" />
              </svg>
            </button>
            <button
              onClick={() => openAddDaireModal('Daire')}
              title="Daire Ekle"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#e67d21', color: '#fff', border: 'none', borderRadius: 8,
                padding: '7px 11px', fontSize: 14, cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f59332')}
              onMouseLeave={e => (e.currentTarget.style.background = '#e67d21')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
        </div>

        {/* Aktif Görüşme Şeridi */}
        {activeGorusme && (
          <div style={{ background: '#0f172a', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #1e293b' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Canlı Görüşme Aktif</span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginLeft: 4 }}>— Müşteri portal üzerinden katılabilir</span>
          </div>
        )}

        {sunum.daireler.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13.5 }}>
            <i className="fas fa-home" style={{ fontSize: 28, display: 'block', marginBottom: 10 }}></i>
            Bu sunumda henüz daire yok.
          </div>
        ) : (
          <div>
            {siraliDaireler.map((sd, idx) => {
              const tepkiBadge = TEPKI_BADGE[sd.musteriTepkisi] || { bg: '#f1f5f9', color: '#475569' };
              const guncelDurum = sd.daire?.daireDurumu || '';
              const guncelDurumMeta = DAIRE_GUNCEL_DURUM_META[guncelDurum];
              const artikMusaitDegil = !!guncelDurumMeta && guncelDurum !== 'Satışta';
              return (
                <div key={sd.sunumDaireID}
                  style={{
                    display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 16,
                    borderBottom: idx < siraliDaireler.length - 1 ? '1px solid #f1f5f9' : 'none',
                    flexWrap: 'wrap',
                    background: guncelDurumMeta ? guncelDurumMeta.rowBg : 'transparent',
                  }}>
                  {/* Daire/Portföy Bilgi */}
                  {sd.sunumOgeTuru === 'Daire' ? (
                    <button
                      onClick={() => setDaireDetayIndex(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 5px', color: '#94a3b8', borderRadius: 5, display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'color 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#1a2440')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                      title="Daire detayını gör"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => sd.portfoyID && openPortfoyDetay(sd.portfoyID)}
                      style={{ width: 26, height: 26, borderRadius: 6, background: '#fdecdc', color: '#e67d21', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#fbd7b5')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#fdecdc')}
                      title="Portföy görselleri ve özellikleri"
                    >
                      <i className="fas fa-building" style={{ fontSize: 12 }} />
                    </button>
                  )}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {sd.sunumOgeTuru === 'Portfoy' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span
                            onClick={() => sd.portfoyID && openPortfoyDetay(sd.portfoyID)}
                            style={{ fontSize: 14, fontWeight: 600, color: '#111827', cursor: 'pointer' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#e67d21')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#111827')}
                            title="Portföy görselleri ve özellikleri"
                          >
                            {sd.portfoy?.baslik || `Portföy #${sd.gayrimenkulID}`}
                          </span>
                          <span style={{ fontSize: 11, background: '#fdecdc', color: '#c2410c', padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>
                            Emlak Portföyü
                          </span>
                          {sd.portfoy?.tur && (
                            <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>
                              {sd.portfoy.tur}
                            </span>
                          )}
                          {sd.portfoy?.durum && (
                            <span style={{
                              fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 600,
                              background: sd.portfoy.durum === 'Kiralik' ? '#f0f9ff' : '#f0fdf4',
                              color: sd.portfoy.durum === 'Kiralik' ? '#0369a1' : '#15803d',
                            }}>
                              {sd.portfoy.durum === 'Kiralik' ? 'Kiralık' : sd.portfoy.durum === 'Satilik' ? 'Satılık' : sd.portfoy.durum}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12.5, color: '#64748b' }}>
                          {sd.portfoy?.adres || `İlan No: ${sd.portfoy?.ilanNo ?? '-'}`}
                        </div>
                        {(sd.portfoy?.fiyat || sd.portfoy?.m2) && (
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {sd.portfoy?.m2 ? `${sd.portfoy.m2} m²` : ''}
                            {sd.portfoy?.m2 && sd.portfoy?.fiyat ? ' · ' : ''}
                            {sd.portfoy?.fiyat ? formatFiyat(sd.portfoy.fiyat) : ''}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Daire {sd.daire?.daireNo || sd.gayrimenkulID}</span>
                          {sd.daire?.daireTipiAdi && (
                            <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 9999, fontWeight: 500 }}>
                              {sd.daire.daireTipiAdi}
                            </span>
                          )}
                          {guncelDurumMeta && (
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 9999,
                              background: guncelDurumMeta.bg, color: guncelDurumMeta.color,
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: guncelDurumMeta.dot, display: 'inline-block', flexShrink: 0 }} />
                              {guncelDurumMeta.label}
                            </span>
                          )}
                          {artikMusaitDegil && (
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <i className="fas fa-triangle-exclamation" style={{ fontSize: 10 }} />
                              Artık müsait değil
                            </span>
                          )}
                        </div>
                        {sd.daire && (
                          <div style={{ fontSize: 12.5, color: '#64748b' }}>
                            {[sd.daire.projeAdi, sd.daire.blokAdi, sd.daire.katAdi, sd.daire.cepheAdi].filter(Boolean).join(' • ')}
                          </div>
                        )}
                        {sd.daire && (sd.daire.listeFiyati || sd.daire.netM2) && (
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {sd.daire.netM2 ? `${sd.daire.netM2} m²` : ''}
                            {sd.daire.netM2 && sd.daire.listeFiyati ? ' · ' : ''}
                            {sd.daire.listeFiyati ? formatFiyat(sd.daire.listeFiyati) : ''}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Müşteri Tepkisi badge */}
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 9999, background: tepkiBadge.bg, color: tepkiBadge.color, whiteSpace: 'nowrap' }}>
                    {sd.musteriTepkisi}
                  </span>

                  {/* Sunum Durumu select */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                    <label style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Sunum Durumu</label>
                    <select
                      value={sd.durum}
                      disabled={updatingDaire === sd.sunumDaireID}
                      onChange={e => handleDaireFieldChange(sd.sunumDaireID, 'durum', e.target.value)}
                      style={{ ...selSt, minWidth: 120 }}
                    >
                      {DAIRE_DURUM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  {/* Müşteri Tepkisi select */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                    <label style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Müşteri Tepkisi</label>
                    <select
                      value={sd.musteriTepkisi}
                      disabled={updatingDaire === sd.sunumDaireID}
                      onChange={e => handleDaireFieldChange(sd.sunumDaireID, 'musteriTepkisi', e.target.value)}
                      style={{ ...selSt, minWidth: 140 }}
                    >
                      {TEPKI_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  {/* Not */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start', flex: 1, minWidth: 180 }}>
                    <label style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Not</label>
                    <textarea
                      value={daireNotlar[sd.sunumDaireID] ?? ''}
                      onChange={e => setDaireNotlar(prev => ({ ...prev, [sd.sunumDaireID]: e.target.value }))}
                      onBlur={() => handleDaireSaveNot(sd.sunumDaireID)}
                      disabled={updatingDaire === sd.sunumDaireID}
                      rows={2}
                      placeholder="Not ekle..."
                      style={{
                        width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 7,
                        fontSize: 12.5, resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                        minWidth: 180, maxWidth: 300, color: '#374151', background: '#fff', lineHeight: 1.5,
                      }}
                      onFocus={e => (e.currentTarget.style.borderColor = '#1a2440')}
                    />
                  </div>

                  {/* Müşteri Görüşü (portalden) */}
                  {(sd.musteriDurumu || sd.musteriNotu) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160, maxWidth: 240 }}>
                      <label style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Müşteri Görüşü</label>
                      {sd.musteriDurumu && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, width: 'fit-content',
                          background: sd.musteriDurumu === 'Olumlu' ? '#dcfce7' : sd.musteriDurumu === 'Olumsuz' ? '#fee2e2' : '#f1f5f9',
                          color: sd.musteriDurumu === 'Olumlu' ? '#15803d' : sd.musteriDurumu === 'Olumsuz' ? '#b91c1c' : '#475569',
                        }}>
                          <i className={`fas fa-${sd.musteriDurumu === 'Olumlu' ? 'thumbs-up' : sd.musteriDurumu === 'Olumsuz' ? 'thumbs-down' : 'minus-circle'}`} style={{ fontSize: 11 }} />
                          {sd.musteriDurumu}
                        </span>
                      )}
                      {sd.musteriNotu && (
                        <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{sd.musteriNotu}</span>
                      )}
                    </div>
                  )}

                  {/* Ödeme Planı — kiralık portföylerde ödeme planı/teklif yapılmaz */}
                  {!(sd.sunumOgeTuru === 'Portfoy' && sd.portfoy?.durum === 'Kiralik') && (
                    <button
                      onClick={() => openOdemePlaniModal(sd)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8', borderRadius: 6, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#e67d21')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                      title="Ödeme Planı"
                    >
                      <i className="fas fa-calendar-alt" style={{ fontSize: 15 }}></i>
                    </button>
                  )}

                  {/* Kaldır */}
                  <button
                    onClick={() => setDeleteConfirm(sd.sunumDaireID)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8', borderRadius: 6, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                    title="Daireyi kaldır"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Paylaşılan Dökümanlar */}
      {paylasimDokumanlar.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.09)', padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-file-export" style={{ color: '#e67d21' }} />
              Paylaşılan Dökümanlar
              <span style={{ background: '#e67d21', color: '#fff', borderRadius: 10, padding: '0 8px', fontSize: 12, fontWeight: 700 }}>{paylasimDokumanlar.length}</span>
            </h4>
            <button onClick={openDokumanModal} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12.5, cursor: 'pointer', color: '#475569', fontWeight: 500 }}>
              <i className="fas fa-pencil-alt" style={{ fontSize: 11 }} /> Düzenle
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {paylasimDokumanlar.map(doc => (
              <div key={doc.dokumanID} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <i className={doc.dokumanTipi === 'Video' ? 'fas fa-film' : 'fas fa-file-alt'} style={{ color: '#6366f1', fontSize: 13 }} />
                  {doc.baslik}
                </div>
                {doc.aciklama && <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{doc.aciklama}</div>}
                {doc.etiketler?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {doc.etiketler.map((e, i) => (
                      <span key={i} style={{ background: e.renk || '#e2e8f0', color: '#fff', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 500 }}>{e.ad}</span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {doc.dokumanTipi !== 'Video' && doc.dosyaYolu && (
                    <>
                      <button onClick={() => setDokumanBelgeViewer(API_BASE_URL + doc.dosyaYolu)} style={{ flex: 1, padding: '5px 0', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                        <i className="fas fa-eye" style={{ marginRight: 4 }} />Görüntüle
                      </button>
                      <a href={API_BASE_URL + doc.dosyaYolu} download={doc.orijinalDosyaAdi || doc.baslik} style={{ flex: 1, textAlign: 'center', padding: '5px 0', background: '#10b981', color: '#fff', borderRadius: 6, fontSize: 12, textDecoration: 'none', fontWeight: 500 }}>
                        <i className="fas fa-download" style={{ marginRight: 4 }} />İndir
                      </a>
                    </>
                  )}
                  {doc.dokumanTipi === 'Video' && doc.videoEmbedKodu && (
                    <button onClick={() => setDokumanBelgeViewer('VIDEO:' + doc.videoEmbedKodu)} style={{ flex: 1, padding: '5px 0', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                      <i className="fas fa-play" style={{ marginRight: 4 }} />İzle
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daire Kaldırma Onayı */}
      {deleteConfirm !== null && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setDeleteConfirm(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 14, padding: 28, maxWidth: 360, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Daireyi Kaldır</h3>
            <p style={{ fontSize: 13.5, color: '#6b7280', marginBottom: 22 }}>Bu daire sunumdan kaldırılacak.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                İptal
              </button>
              <button onClick={() => handleRemoveDaire(deleteConfirm)}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 600 }}>
                Kaldır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daire Ekleme Modalı */}
      {showAddDaireModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowAddDaireModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'relative', width: '100%', maxWidth: 1040, margin: '0 16px', maxHeight: '90vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ flexShrink: 0, padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Öğe Ekle</h2>
                <p style={{ fontSize: 12.5, color: '#6b7280', margin: '3px 0 0' }}>
                  {addOgeTuru === 'Daire' ? 'Satışta olan dairelerden seçin.'
                    : addOgeTuru === 'Portfoy' ? 'Emlak portföyünden seçin.'
                    : 'Aradığınız gayrimenkulü kendi cümlenizle yazın.'}
                </p>
              </div>
              <button onClick={() => setShowAddDaireModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                <X size={20} />
              </button>
            </div>

            {/* Kaynak sekmeleri */}
            <div style={{ flexShrink: 0, display: 'flex', gap: 4, padding: '12px 24px 0' }}>
              <button onClick={() => setAddOgeTuru('Daire')}
                style={{
                  padding: '8px 16px', border: 'none', borderBottom: addOgeTuru === 'Daire' ? '2px solid #1a2440' : '2px solid transparent',
                  background: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  color: addOgeTuru === 'Daire' ? '#1a2440' : '#94a3b8',
                }}>
                <i className="fas fa-home" style={{ marginRight: 6 }} />Projeden Daire
                {selectedDaireIDs.length > 0 && <span style={{ marginLeft: 6, background: '#1a2440', color: '#fff', borderRadius: 9999, padding: '1px 7px', fontSize: 11 }}>{selectedDaireIDs.length}</span>}
              </button>
              <button onClick={() => setAddOgeTuru('Portfoy')}
                style={{
                  padding: '8px 16px', border: 'none', borderBottom: addOgeTuru === 'Portfoy' ? '2px solid #e67d21' : '2px solid transparent',
                  background: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  color: addOgeTuru === 'Portfoy' ? '#e67d21' : '#94a3b8',
                }}>
                <i className="fas fa-building" style={{ marginRight: 6 }} />Emlak Portföyü
                {selectedPortfoyIDs.length > 0 && <span style={{ marginLeft: 6, background: '#e67d21', color: '#fff', borderRadius: 9999, padding: '1px 7px', fontSize: 11 }}>{selectedPortfoyIDs.length}</span>}
              </button>
              <button onClick={() => setAddOgeTuru('Akilli')}
                style={{
                  padding: '8px 16px', border: 'none', borderBottom: addOgeTuru === 'Akilli' ? '2px solid #7c3aed' : '2px solid transparent',
                  background: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  color: addOgeTuru === 'Akilli' ? '#7c3aed' : '#94a3b8',
                }}>
                <i className="fas fa-wand-magic-sparkles" style={{ marginRight: 6 }} />Akıllı Arama
              </button>
            </div>

            {/* Modal Body — minHeight:0, flex çocuğunun içeriğinden küçülüp kaydırma yapabilmesi için şart */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {addOgeTuru === 'Daire' ? (
                <>
                  {/* Filtreler */}
                  {(() => {
                    const filtreSt = (secili: string): React.CSSProperties => ({
                      padding: '7px 10px', border: `1px solid ${secili ? '#1a2440' : '#d1d5db'}`, borderRadius: 7, fontSize: 12.5,
                      background: secili ? '#f1f5ff' : '#fff', color: '#374151', outline: 'none', minWidth: 0,
                    });
                    const aktifFiltre = [addFilterProjeID, addFilterBlokID, addFilterKatID, addFilterCepheID, addFilterManzaraID, addFilterTipID, addDaireSearch].filter(Boolean).length;
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
                        <select value={addFilterProjeID} style={filtreSt(addFilterProjeID)}
                          onChange={e => { setAddFilterProjeID(e.target.value); setAddFilterBlokID(''); setAddFilterKatID(''); setAddFilterCepheID(''); setAddFilterManzaraID(''); setAddFilterTipID(''); }}>
                          <option value="">Tüm Projeler</option>
                          {addProjeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                        </select>
                        {/* Farklı projelerde aynı adlı bloklar olabildiği için blok ancak proje seçilince seçilebilir */}
                        <select value={addFilterBlokID} disabled={!addFilterProjeID}
                          title={!addFilterProjeID ? 'Blok seçmek için önce bir proje seçin' : undefined}
                          style={{ ...filtreSt(addFilterBlokID), ...(!addFilterProjeID ? { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } : {}) }}
                          onChange={e => { setAddFilterBlokID(e.target.value); setAddFilterKatID(''); }}>
                          <option value="">{addFilterProjeID ? 'Tüm Bloklar' : 'Blok (önce proje seçin)'}</option>
                          {addBloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                        </select>
                        <select value={addFilterKatID} onChange={e => setAddFilterKatID(e.target.value)} style={filtreSt(addFilterKatID)}>
                          <option value="">Tüm Katlar</option>
                          {addKatlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                        </select>
                        <select value={addFilterTipID} onChange={e => setAddFilterTipID(e.target.value)} style={filtreSt(addFilterTipID)}>
                          <option value="">Tüm Daire Tipleri</option>
                          {addTipler.map(dt => <option key={dt.daireTipID} value={dt.daireTipID}>{dt.tip}</option>)}
                        </select>
                        <select value={addFilterCepheID} onChange={e => setAddFilterCepheID(e.target.value)} style={filtreSt(addFilterCepheID)}>
                          <option value="">Tüm Cepheler</option>
                          {addCepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                        </select>
                        <select value={addFilterManzaraID} onChange={e => setAddFilterManzaraID(e.target.value)} style={filtreSt(addFilterManzaraID)}>
                          <option value="">Tüm Manzaralar</option>
                          {addManzaralar.map(m => <option key={m.manzaraID} value={m.manzaraID}>{m.manzaraAdi}</option>)}
                        </select>
                        <input type="text" placeholder="Daire ara..." value={addDaireSearch} onChange={e => setAddDaireSearch(e.target.value)} style={filtreSt(addDaireSearch)} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#64748b' }}>
                          <span><b style={{ color: '#1a2440' }}>{filteredAddDaireler.length}</b> daire</span>
                          {aktifFiltre > 0 && (
                            <button onClick={() => { setAddFilterProjeID(''); setAddFilterBlokID(''); setAddFilterKatID(''); setAddFilterCepheID(''); setAddFilterManzaraID(''); setAddFilterTipID(''); setAddDaireSearch(''); }}
                              style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              <i className="fas fa-times" style={{ marginRight: 4 }}></i>Temizle
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {selectedDaireIDs.length > 0 && (
                    <div style={{ fontSize: 12.5, color: '#059669', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '6px 12px' }}>
                      {selectedDaireIDs.length} daire seçildi
                    </div>
                  )}

                  {/* Daire listesi */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                    {filteredAddDaireler.length === 0 ? (
                      <div style={{ padding: '20px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                        {tumDaireler.length === 0 ? t('msg.yukleniyor') : 'Eklenecek satışta daire bulunamadı'}
                      </div>
                    ) : filteredAddDaireler.map((d, idx) => {
                      const selected = selectedDaireIDs.includes(d.daireID);
                      return (
                        <div key={d.daireID}
                          onClick={() => setSelectedDaireIDs(prev => prev.includes(d.daireID) ? prev.filter(id => id !== d.daireID) : [...prev, d.daireID])}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: idx < filteredAddDaireler.length - 1 ? '1px solid #f1f5f9' : 'none',
                            background: selected ? '#eff6ff' : 'transparent', transition: 'background 0.12s',
                          }}
                          onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                          <input type="checkbox" readOnly checked={selected}
                            style={{ accentColor: '#1a2440', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>Daire {d.daireNo}</span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 10 }}>
                              {[d.proje?.projeAdi, d.blok?.blokAdi, d.kat?.katAdi, d.daireTipi?.tip, d.cephe?.cepheAdi, d.manzara?.manzaraAdi].filter(Boolean).join(' • ')}
                            </span>
                          </div>
                          {d.listeFiyati && (
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#1a2440', whiteSpace: 'nowrap' }}>
                              {d.listeFiyati.toLocaleString('tr-TR')} ₺
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : addOgeTuru === 'Portfoy' ? (
                <>
                  <input type="text" placeholder="Portföy ara (başlık, adres, ilan no)..." value={addPortfoySearch} onChange={e => setAddPortfoySearch(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12.5, background: '#fff', color: '#374151', outline: 'none' }} />

                  {selectedPortfoyIDs.length > 0 && (
                    <div style={{ fontSize: 12.5, color: '#c2410c', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 7, padding: '6px 12px' }}>
                      {selectedPortfoyIDs.length} portföy seçildi
                    </div>
                  )}

                  {/* Portföy listesi */}
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                    {filteredAddPortfoyler.length === 0 ? (
                      <div style={{ padding: '20px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                        {tumPortfoyler.length === 0 ? t('msg.yukleniyor') : 'Eklenecek portföy bulunamadı'}
                      </div>
                    ) : filteredAddPortfoyler.map((p, idx) => {
                      const selected = selectedPortfoyIDs.includes(p.portfoyID);
                      return (
                        <div key={p.portfoyID}
                          onClick={() => setSelectedPortfoyIDs(prev => prev.includes(p.portfoyID) ? prev.filter(id => id !== p.portfoyID) : [...prev, p.portfoyID])}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: idx < filteredAddPortfoyler.length - 1 ? '1px solid #f1f5f9' : 'none',
                            background: selected ? '#fff7ed' : 'transparent', transition: 'background 0.12s',
                          }}
                          onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                          <input type="checkbox" readOnly checked={selected}
                            style={{ accentColor: '#e67d21', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{p.baslik}</span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 10 }}>
                              {p.adres || `İlan No: ${p.ilanNo}`} · {p.tur}
                            </span>
                          </div>
                          {p.fiyat > 0 && (
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#1a2440', whiteSpace: 'nowrap' }}>
                              {p.fiyat.toLocaleString('tr-TR')} ₺
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                /* ---------- Akıllı Arama ---------- */
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <textarea
                      rows={2}
                      value={akilliMetin}
                      onChange={e => setAkilliMetin(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAkilliAra(); }}
                      placeholder="Örn: 5-7 milyon aralığında 2+1 sıfır daire arıyor"
                      style={{
                        flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 9,
                        fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                        color: '#374151', background: '#fff', lineHeight: 1.5,
                      }}
                      onFocus={e => (e.currentTarget.style.borderColor = '#7c3aed')}
                      onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                    />
                    <button
                      onClick={handleAkilliAra}
                      disabled={akilliLoading || akilliMetin.trim().length < 3}
                      style={{
                        padding: '10px 20px', border: 'none', borderRadius: 9, flexShrink: 0,
                        background: akilliLoading || akilliMetin.trim().length < 3 ? '#c4b5fd' : '#7c3aed',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: akilliLoading || akilliMetin.trim().length < 3 ? 'not-allowed' : 'pointer',
                      }}>
                      <i className={akilliLoading ? 'fas fa-spinner fa-spin' : 'fas fa-magnifying-glass'} style={{ marginRight: 6 }} />
                      {akilliLoading ? 'Aranıyor...' : 'Ara'}
                    </button>
                  </div>

                  {!akilliKriterler && !akilliLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>Örnek aramalar:</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {['5-7 milyon aralığında 2+1 sıfır daire arıyor', 'yeni bir projede 4+1 10 milyon bütçesi var'].map(ornek => (
                          <button key={ornek} onClick={() => { setAkilliMetin(ornek); setTimeout(handleAkilliAra, 0); }}
                            style={{ padding: '6px 12px', border: '1px solid #ddd6fe', borderRadius: 9999, background: '#f5f3ff', color: '#6d28d9', fontSize: 12, cursor: 'pointer' }}>
                            {ornek}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {akilliKriterler && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>Anladığım kriterler:</span>
                      {kriterEtiketleri(akilliKriterler).map(c => (
                        <span key={c.alan + c.etiket}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' }}>
                          {c.etiket}
                          <span onClick={() => handleKriterSil(c.alan)} title="Bu kriteri kaldır"
                            style={{ cursor: 'pointer', color: '#a78bfa', fontWeight: 700 }}>×</span>
                        </span>
                      ))}
                      <span
                        title="Yapay zekaya yalnızca yazdığınız cümle gönderildi; müşteri, daire veya portföy verisi gönderilmedi."
                        style={{
                          marginLeft: 'auto', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
                          background: akilliKaynak === 'ai' ? '#f5f3ff' : '#f1f5f9',
                          color: akilliKaynak === 'ai' ? '#7c3aed' : '#64748b',
                          whiteSpace: 'nowrap',
                        }}>
                        ● {akilliKaynak === 'ai' ? 'AI ile yorumlandı' : 'Yerel çözümleme'}
                      </span>
                    </div>
                  )}

                  {akilliHata && (
                    <div style={{ fontSize: 12.5, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                      <i className="fas fa-circle-exclamation" style={{ marginRight: 6 }} />{akilliHata}
                    </div>
                  )}

                  {akilliKriterler && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                      {akilliSonuclar.length === 0 ? (
                        <div style={{ padding: '20px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                          Eklenebilecek daire veya portföy bulunamadı.
                        </div>
                      ) : akilliSonuclar.map((s, idx) => {
                        const secili = s.tur === 'Daire' ? selectedDaireIDs.includes(s.id) : selectedPortfoyIDs.includes(s.id);
                        const oncekiTam = idx > 0 ? akilliSonuclar[idx - 1].tamEslesme : true;
                        const ayiricGoster = !s.tamEslesme && oncekiTam;
                        return (
                          <React.Fragment key={`${s.tur}-${s.id}`}>
                            {ayiricGoster && (
                              <div style={{ padding: '6px 14px', background: '#f8fafc', fontSize: 11.5, color: '#94a3b8', fontWeight: 600, borderTop: idx > 0 ? '1px solid #e5e7eb' : 'none' }}>
                                Tam eşleşen bulunamadı — en yakın sonuçlar
                              </div>
                            )}
                            <div
                              onClick={() => {
                                if (s.tur === 'Daire') setSelectedDaireIDs(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                                else setSelectedPortfoyIDs(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                              }}
                              style={{
                                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', cursor: 'pointer',
                                borderBottom: idx < akilliSonuclar.length - 1 ? '1px solid #f1f5f9' : 'none',
                                background: secili ? (s.tur === 'Daire' ? '#eff6ff' : '#fff7ed') : 'transparent',
                              }}
                              onMouseEnter={e => { if (!secili) e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={e => { if (!secili) e.currentTarget.style.background = 'transparent'; }}>
                              <input type="checkbox" readOnly checked={secili}
                                style={{ accentColor: s.tur === 'Daire' ? '#1a2440' : '#e67d21', width: 15, height: 15, cursor: 'pointer', flexShrink: 0, marginTop: 3 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 9999, background: s.tur === 'Daire' ? '#eff6ff' : '#fff7ed', color: s.tur === 'Daire' ? '#1d4ed8' : '#c2410c' }}>
                                    {s.tur === 'Daire' ? 'Daire' : 'Portföy'}
                                  </span>
                                  <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{s.baslik}</span>
                                </div>
                                {s.altBaslik && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.altBaslik}</div>}
                                {s.nedenler.length > 0 && (
                                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
                                    {s.nedenler.map((n, i) => (
                                      <span key={i} style={{
                                        fontSize: 10.5, padding: '2px 8px', borderRadius: 9999,
                                        background: n.ton === 'iyi' ? '#f0fdf4' : n.ton === 'uyari' ? '#fff7ed' : '#f1f5f9',
                                        color: n.ton === 'iyi' ? '#15803d' : n.ton === 'uyari' ? '#c2410c' : '#475569',
                                      }}>{n.metin}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                {s.fiyat != null && s.fiyat > 0 && (
                                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a2440', whiteSpace: 'nowrap' }}>
                                    {s.fiyat.toLocaleString('tr-TR')} ₺
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', marginTop: 4 }}>
                                  <span style={{ width: 44, height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden', display: 'inline-block' }}>
                                    <span style={{ display: 'block', height: '100%', width: `${s.skor}%`, background: s.skor >= 45 ? '#16a34a' : '#f59e0b' }} />
                                  </span>
                                  <span style={{ fontSize: 11, color: '#94a3b8' }}>%{s.skor}</span>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ flexShrink: 0, padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowAddDaireModal(false)}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                İptal
              </button>
              <button onClick={handleAddDaireler} disabled={addingDaireler || (selectedDaireIDs.length === 0 && selectedPortfoyIDs.length === 0)}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: 8,
                  background: addingDaireler || (selectedDaireIDs.length === 0 && selectedPortfoyIDs.length === 0) ? '#94a3b8' : '#1a2440',
                  color: '#fff', fontSize: 13.5, cursor: addingDaireler || (selectedDaireIDs.length === 0 && selectedPortfoyIDs.length === 0) ? 'not-allowed' : 'pointer',
                  fontWeight: 600, transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (!addingDaireler && (selectedDaireIDs.length > 0 || selectedPortfoyIDs.length > 0)) e.currentTarget.style.background = '#e67d21'; }}
                onMouseLeave={e => { if (!addingDaireler && (selectedDaireIDs.length > 0 || selectedPortfoyIDs.length > 0)) e.currentTarget.style.background = '#1a2440'; }}>
                {addingDaireler ? 'Ekleniyor...' : `Ekle (${selectedDaireIDs.length + selectedPortfoyIDs.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Portföy Detay Modalı — görseller + özellikler */}
      {(portfoyDetay || portfoyDetayLoading) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => { setPortfoyDetay(null); setPortfoyDetayLoading(false); }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
          <div style={{ position: 'relative', width: '96%', maxWidth: 1040, maxHeight: '92vh', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>

            {portfoyDetayLoading || !portfoyDetay ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Yükleniyor...
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#e67d21', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
                      <i className="fas fa-building" style={{ marginRight: 6 }} />Emlak Portföyü
                    </div>
                    <h2 style={{ fontSize: 21, fontWeight: 800, color: '#111827', margin: 0 }}>{portfoyDetay.baslik}</h2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => navigate(`/emlak-portfoy/${portfoyDetay.portfoyID}`)}
                      style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '7px 10px', color: '#475569', borderRadius: 8, display: 'flex', alignItems: 'center' }}
                      title="Portföy detay sayfası">
                      <i className="fas fa-external-link-alt" style={{ fontSize: 13 }} />
                    </button>
                    <button onClick={() => setPortfoyDetay(null)}
                      style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '7px 10px', color: '#475569', borderRadius: 8, display: 'flex', alignItems: 'center' }}>
                      <X size={17} />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
                  {/* Galeri */}
                  <div>
                    {portfoyDetay.fotograflar && portfoyDetay.fotograflar.length > 0 ? (
                      <>
                        <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#0f172a', aspectRatio: '4 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={portfoyFotoUrl(portfoyDetay.fotograflar[portfoyAktifFoto]?.dosyaYolu)} alt={portfoyDetay.baslik}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          {portfoyDetay.fotograflar.length > 1 && (
                            <>
                              <button onClick={() => setPortfoyAktifFoto(p => p === 0 ? portfoyDetay.fotograflar.length - 1 : p - 1)}
                                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.85)', cursor: 'pointer', color: '#1a2440' }}>
                                <i className="fas fa-chevron-left" />
                              </button>
                              <button onClick={() => setPortfoyAktifFoto(p => p === portfoyDetay.fotograflar.length - 1 ? 0 : p + 1)}
                                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.85)', cursor: 'pointer', color: '#1a2440' }}>
                                <i className="fas fa-chevron-right" />
                              </button>
                              <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(15,23,42,0.75)', color: '#fff', fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 9999 }}>
                                {portfoyAktifFoto + 1} / {portfoyDetay.fotograflar.length}
                              </div>
                            </>
                          )}
                        </div>
                        {portfoyDetay.fotograflar.length > 1 && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            {portfoyDetay.fotograflar.map((foto, idx) => (
                              <div key={foto.fotoID} onClick={() => setPortfoyAktifFoto(idx)}
                                style={{
                                  width: 66, height: 50, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                                  border: idx === portfoyAktifFoto ? '2px solid #e67d21' : '2px solid transparent',
                                  opacity: idx === portfoyAktifFoto ? 1 : 0.65,
                                }}>
                                <img src={portfoyFotoUrl(foto.dosyaYolu)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ borderRadius: 12, background: '#f1f5f9', aspectRatio: '4 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 8 }}>
                        <i className="fas fa-image" style={{ fontSize: 30 }} />
                        <span style={{ fontSize: 13 }}>Fotoğraf yok</span>
                      </div>
                    )}
                  </div>

                  {/* Bilgiler */}
                  <div>
                    <div style={{ fontSize: 25, fontWeight: 800, color: '#1a2440', letterSpacing: '-0.02em' }}>
                      {portfoyDetay.fiyat.toLocaleString('tr-TR')} ₺
                    </div>
                    {portfoyDetay.m2 > 0 && (
                      <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>
                        {Math.round(portfoyDetay.fiyat / portfoyDetay.m2).toLocaleString('tr-TR')} ₺/m²
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', margin: '14px 0' }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#eff6ff', color: '#1d4ed8' }}>{portfoyDetay.tur}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: '#fff7ed', color: '#c2410c' }}>{portfoyDetay.durum}</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                        <span style={{ color: '#64748b' }}><i className="fas fa-ruler-combined" style={{ marginRight: 6, color: '#94a3b8' }} />Alan</span>
                        <span style={{ color: '#111827', fontWeight: 600 }}>{portfoyDetay.m2} m²</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                        <span style={{ color: '#64748b' }}><i className="fas fa-hashtag" style={{ marginRight: 6, color: '#94a3b8' }} />İlan No</span>
                        <span style={{ color: '#111827', fontWeight: 600 }}>{portfoyDetay.ilanNo}</span>
                      </div>
                      {portfoyDetay.adres && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                          <span style={{ color: '#64748b', flexShrink: 0 }}><i className="fas fa-map-marker-alt" style={{ marginRight: 6, color: '#94a3b8' }} />Adres</span>
                          <span style={{ color: '#111827', fontWeight: 600, textAlign: 'right' }}>{portfoyDetay.adres}</span>
                        </div>
                      )}
                    </div>

                    {portfoyDetay.ozellikler && portfoyDetay.ozellikler.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2440', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 9 }}>
                          <i className="fas fa-tags" style={{ marginRight: 6, color: '#e67d21' }} />Özellikler
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {portfoyDetay.ozellikler.map(oz => (
                            <span key={oz.ozellikID} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#334155' }}>
                              <i className="fas fa-check" style={{ marginRight: 5, color: '#16a34a', fontSize: 10 }} />{oz.ozellikAdi}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {portfoyDetay.aciklama && (
                      <div style={{ marginTop: 18 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2440', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 7 }}>
                          <i className="fas fa-align-left" style={{ marginRight: 6, color: '#e67d21' }} />Açıklama
                        </div>
                        <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{portfoyDetay.aciklama}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Daire Detay Modalı */}
      {daireDetayIndex !== null && sunum && (() => {
        const sd = siraliDaireler[daireDetayIndex];
        const hasPrev = daireDetayIndex > 0;
        const hasNext = daireDetayIndex < siraliDaireler.length - 1;
        const tepkiBadge = TEPKI_BADGE[sd.musteriTepkisi] || { bg: '#f1f5f9', color: '#475569' };
        const daireDurumColors: Record<string, { bg: string; color: string; dot: string }> = {
          'Satışta':  { bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
          'Satıldı':  { bg: '#fff1f2', color: '#be123c', dot: '#ef4444' },
          'Rezerve':  { bg: '#fff7ed', color: '#c2410c', dot: '#f97316' },
        };
        const ddColor = daireDurumColors[sd.daire?.daireDurumu || ''] || { bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' };
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            onClick={() => setDaireDetayIndex(null)}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
            <div style={{ position: 'relative', width: '96%', maxWidth: 1140, height: '92vh', maxHeight: '92vh', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}>

              {/* Header — white */}
              <div style={{ background: '#fff', padding: '16px 24px 0', borderBottom: '1px solid #e2e8f0', flexShrink: 0, position: 'relative' }}>
                {sd.daire?.projeAdi && (
                  <div style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 500, marginBottom: 4 }}>{sd.daire.projeAdi}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>
                    Daire {sd.daire?.daireNo || sd.daireID}
                  </h2>
                  {sd.daire?.daireTipiAdi && (
                    <span style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 9999, background: ddColor.bg, color: ddColor.color, border: `1px solid ${ddColor.dot}55`, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: ddColor.dot, display: 'inline-block', flexShrink: 0 }} />
                      {sd.daire.daireTipiAdi}
                    </span>
                  )}
                  <span style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 9999, background: ddColor.bg, color: ddColor.color, border: `1px solid ${ddColor.dot}55`, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: ddColor.dot, display: 'inline-block', flexShrink: 0 }} />
                    {sd.daire?.daireDurumu || 'Satışta'}
                  </span>
                </div>
                {sd.daire?.takipNo && (
                  <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 5 }}>
                    Takip No <strong style={{ color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{sd.daire.takipNo}</strong>
                  </div>
                )}
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, marginTop: 14 }}>
                  {(['galeri', 'gorunum'] as const).map((tab) => (
                    <button key={tab}
                      onClick={async () => {
                        setDaireDetayTab(tab);
                        if (tab === 'gorunum' && sd.daire?.projeID && daireGorunumlar.length === 0) {
                          setGorunumLoading(true);
                          try {
                            const res = await axiosInstance.get(`/api/ProjeGorunumleri?projeID=${sd.daire.projeID}`);
                            setDaireGorunumlar((res.data as GVGorunum[]).filter(g => g.daireler.some(d => d.daireID === sd.daire!.daireID)));
                          } catch { /* sessiz */ }
                          finally { setGorunumLoading(false); }
                        }
                      }}
                      style={{
                        padding: '8px 18px', fontSize: 13, fontWeight: 600,
                        border: 'none', background: 'none', cursor: 'pointer',
                        borderBottom: daireDetayTab === tab ? '2px solid #e67d21' : '2px solid transparent',
                        color: daireDetayTab === tab ? '#e67d21' : '#64748b',
                        transition: 'color 0.15s',
                      }}
                    >
                      {tab === 'galeri' ? (
                        <><i className="fas fa-images" style={{ marginRight: 6 }}></i>Galeri &amp; Detaylar</>
                      ) : (
                        <><i className="fas fa-layer-group" style={{ marginRight: 6 }}></i>Görünümler</>
                      )}
                    </button>
                  ))}
                </div>
                <button onClick={() => setDaireDetayIndex(null)}
                  style={{ position: 'absolute', top: 14, right: 20, background: 'transparent', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '5px 7px', color: '#6b7280', borderRadius: 8, display: 'flex', alignItems: 'center', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                  <X size={18} />
                </button>
              </div>

              {/* Body — two columns */}
              <div style={{ flex: 1, overflow: 'hidden', display: daireDetayTab === 'gorunum' ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: '3fr 2fr', minHeight: 0 }}>

                {/* ── GÖRÜNÜMLER TAB ── */}
                {daireDetayTab === 'gorunum' && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {gorunumLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#64748b', flex: 1 }}>
                        <i className="fas fa-spinner fa-spin"></i> Görünümler yükleniyor...
                      </div>
                    ) : daireGorunumlar.length === 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#94a3b8', flex: 1 }}>
                        <i className="fas fa-layer-group" style={{ fontSize: 36 }}></i>
                        <span style={{ fontSize: 14 }}>Bu daire için tanımlanmış görünüm bulunamadı.</span>
                      </div>
                    ) : (() => {
                      const g = daireGorunumlar[selectedGorunumIdx] ?? daireGorunumlar[0];
                      const gd = g.daireler.find(d => d.daireID === sd.daire!.daireID);
                      const durumColors: Record<string, string> = { 'Satışta': '#22c55e', 'Rezerve': '#f59e0b', 'Sözleşme': '#3b82f6' };
                      const dotColor = durumColors[sd.daire?.daireDurumu || ''] || '#94a3b8';
                      return (
                        <>
                          {/* View switcher — tabs if ≤5, dropdown if more */}
                          {daireGorunumlar.length > 1 && (
                            <div style={{ flexShrink: 0, padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto' }}>
                              {daireGorunumlar.length <= 5
                                ? daireGorunumlar.map((gr, i) => (
                                    <button key={gr.gorunumID}
                                      onClick={() => setSelectedGorunumIdx(i)}
                                      style={{
                                        padding: '5px 14px', fontSize: 12.5, fontWeight: 600,
                                        border: selectedGorunumIdx === i ? '1.5px solid #e67d21' : '1.5px solid #e2e8f0',
                                        borderRadius: 20, background: selectedGorunumIdx === i ? '#fff4ea' : '#fff',
                                        color: selectedGorunumIdx === i ? '#e67d21' : '#64748b',
                                        cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                                      }}
                                    >{gr.gorunumAdi}</button>
                                  ))
                                : (
                                  <select
                                    value={selectedGorunumIdx}
                                    onChange={e => setSelectedGorunumIdx(Number(e.target.value))}
                                    style={{ fontSize: 13, padding: '5px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer' }}
                                  >
                                    {daireGorunumlar.map((gr, i) => (
                                      <option key={gr.gorunumID} value={i}>{gr.gorunumAdi}</option>
                                    ))}
                                  </select>
                                )
                              }
                            </div>
                          )}

                          {/* Full-width canvas — 4:3 inner wrapper */}
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#0f172a', minHeight: 0 }}>
                            <div style={{ position: 'relative', aspectRatio: '4/3', maxHeight: '100%', maxWidth: '100%', flexShrink: 0 }}>
                            {g.arkaplanGorsel
                              ? <img src={`${API_BASE_URL}/api/ProjeGorunumleri/gorsel/${g.arkaplanGorsel}`} alt=""
                                  style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }} />
                              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', flexDirection: 'column', gap: 8, background: '#1e293b' }}>
                                  <i className="fas fa-image" style={{ fontSize: 40 }}></i>
                                  <span style={{ fontSize: 13 }}>Arka plan yok</span>
                                </div>
                            }
                            {/* Rect */}
                            {gd && gd.rxMin != null && gd.ryMin != null && gd.rxMax != null && gd.ryMax != null && (
                              <div style={{
                                position: 'absolute',
                                left: `${gd.rxMin}%`, top: `${gd.ryMin}%`,
                                width: `${gd.rxMax! - gd.rxMin!}%`, height: `${gd.ryMax! - gd.ryMin!}%`,
                                border: `2px solid ${dotColor}`,
                                borderRadius: 4, boxSizing: 'border-box',
                                background: hoveredGorunumDaireID === g.gorunumID ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.08)',
                                transition: 'background 0.15s',
                                zIndex: 6,
                              }} />
                            )}
                            {/* Other daires (dimmed) */}
                            {g.daireler.filter(d => d.daireID !== sd.daire?.daireID).map(d => (
                              <div key={d.daireID} style={{
                                position: 'absolute',
                                left: `${d.xYuzde}%`, top: `${d.yYuzde}%`,
                                transform: 'translate(-50%,-50%)',
                                width: 22, height: 22, borderRadius: '50%',
                                background: 'rgba(148,163,184,0.55)',
                                border: '2px solid rgba(255,255,255,0.5)',
                                zIndex: 7,
                              }} />
                            ))}
                            {/* This daire dot */}
                            {gd && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: `${gd.xYuzde}%`, top: `${gd.yYuzde}%`,
                                  transform: 'translate(-50%,-50%)',
                                  width: 36, height: 36, borderRadius: '50%',
                                  background: dotColor,
                                  border: '3px solid rgba(255,255,255,0.9)',
                                  boxShadow: `0 0 0 2px ${dotColor}, 0 4px 12px rgba(0,0,0,0.4)`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 8, fontWeight: 800, color: '#fff',
                                  zIndex: 10, cursor: 'default',
                                }}
                                onMouseEnter={() => setHoveredGorunumDaireID(g.gorunumID)}
                                onMouseLeave={() => setHoveredGorunumDaireID(null)}
                              >
                                {(sd.daire?.daireNo || '').slice(0, 4)}
                              </div>
                            )}
                            {/* View name badge */}
                            <div style={{ position: 'absolute', bottom: 14, left: 14, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '5px 12px', borderRadius: 20, backdropFilter: 'blur(6px)', zIndex: 12 }}>
                              <i className="fas fa-layer-group" style={{ marginRight: 6, opacity: 0.8 }}></i>{g.gorunumAdi}
                            </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Left — Gallery */}
                <div style={{ padding: '18px 20px 16px', borderRight: '1px solid #e2e8f0', display: daireDetayTab === 'gorunum' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', gap: 10 }}>
                  {galeriGorseller.length > 0 ? (
                    <>
                      {/* Main Image */}
                      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#0f172a', flex: 1 }}>
                        <img
                          src={`${API_BASE_URL}/api/SunumBelgeleri/download/${galeriGorseller[galeriSlideIndex]?.sunumBelgeId}`}
                          alt={galeriGorseller[galeriSlideIndex]?.aciklama || ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 220 }}
                        />
                        {/* Counter badge */}
                        <div style={{ position: 'absolute', top: 12, right: 14, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>
                          {galeriSlideIndex + 1} / {galeriGorseller.length}
                        </div>
                        {galeriGorseller[galeriSlideIndex]?.aciklama && (
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.65))', color: '#fff', padding: '20px 16px 10px', fontSize: 12.5, fontWeight: 600 }}>
                            {galeriGorseller[galeriSlideIndex].aciklama}
                          </div>
                        )}
                        {galeriGorseller.length > 1 && (
                          <>
                            <button onClick={() => setGaleriSlideIndex(i => i <= 0 ? galeriGorseller.length - 1 : i - 1)}
                              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', fontSize: 20, fontWeight: 700, color: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>‹</button>
                            <button onClick={() => setGaleriSlideIndex(i => i >= galeriGorseller.length - 1 ? 0 : i + 1)}
                              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', fontSize: 20, fontWeight: 700, color: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>›</button>
                          </>
                        )}
                      </div>
                      {/* Thumbnails */}
                      {galeriGorseller.length > 1 && (
                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2, flexShrink: 0 }}>
                          {galeriGorseller.map((g, i) => (
                            <img
                              key={g.sunumBelgeId}
                              src={`${API_BASE_URL}/api/SunumBelgeleri/download/${g.sunumBelgeId}`}
                              alt={g.aciklama || ''}
                              onClick={() => setGaleriSlideIndex(i)}
                              style={{
                                width: 68, height: 52, objectFit: 'cover', borderRadius: 7, cursor: 'pointer', flexShrink: 0,
                                border: i === galeriSlideIndex ? '2px solid #e67d21' : '2px solid transparent',
                                opacity: i === galeriSlideIndex ? 1 : 0.65,
                                transition: 'all 0.15s',
                              }}
                            />
                          ))}
                        </div>
                      )}
                      {/* Belgeler + Videolar */}
                      {(galeriBelgeler.length > 0 || galeriVideolar.length > 0) && (
                        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {galeriBelgeler.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Belgeler</div>
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                {galeriBelgeler.map((b, i) => (
                                  <div key={b.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: i < galeriBelgeler.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                    <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 13, flexShrink: 0 }}></i>
                                    <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                                    <a href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`} target="_blank" rel="noreferrer"
                                      style={{ fontSize: 11, color: '#1a2440', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                                      <i className="fas fa-download"></i> İndir
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {galeriVideolar.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Videolar</div>
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                {galeriVideolar.map((v, i) => (
                                  <div key={v.sunumBelgeId}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: i < galeriVideolar.length - 1 ? '1px solid #f1f5f9' : 'none', transition: 'background 0.12s' }}
                                    onClick={() => setShowVideoModal(v)}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                    <i className="fas fa-play-circle" style={{ color: '#dc2626', fontSize: 15, flexShrink: 0 }}></i>
                                    <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{v.aciklama || 'Video'}</span>
                                    <span style={{ fontSize: 11, color: '#e67d21', fontWeight: 600 }}>Oynat →</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#94a3b8', gap: 10 }}>
                      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>Görsel bulunamadı</div>
                      {(galeriBelgeler.length > 0 || galeriVideolar.length > 0) && (
                        <div style={{ width: '100%', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {galeriBelgeler.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Belgeler</div>
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                {galeriBelgeler.map((b, i) => (
                                  <div key={b.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: i < galeriBelgeler.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                    <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 13, flexShrink: 0 }}></i>
                                    <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                                    <a href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`} target="_blank" rel="noreferrer"
                                      style={{ fontSize: 11, color: '#1a2440', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                                      <i className="fas fa-download"></i> İndir
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {galeriVideolar.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Videolar</div>
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                {galeriVideolar.map((v, i) => (
                                  <div key={v.sunumBelgeId}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: i < galeriVideolar.length - 1 ? '1px solid #f1f5f9' : 'none', transition: 'background 0.12s' }}
                                    onClick={() => setShowVideoModal(v)}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                    <i className="fas fa-play-circle" style={{ color: '#dc2626', fontSize: 15, flexShrink: 0 }}></i>
                                    <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{v.aciklama || 'Video'}</span>
                                    <span style={{ fontSize: 11, color: '#e67d21', fontWeight: 600 }}>Oynat →</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right — Price + Details */}
                <div style={{ overflowY: 'auto', padding: '18px 20px', display: daireDetayTab === 'gorunum' ? 'none' : 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Price Card */}
                  {sd.daire?.listeFiyati ? (
                    <div style={{ background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 14, padding: '18px 20px' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#e67d21', lineHeight: 1.1 }}>
                        {sd.daire.listeFiyati.toLocaleString('tr-TR')} ₺
                      </div>
                      {sd.daire.netM2 && (
                        <div style={{ fontSize: 13, color: '#e67d21', marginTop: 3, fontWeight: 500 }}>
                          {Math.round(sd.daire.listeFiyati / sd.daire.netM2).toLocaleString('tr-TR')} ₺/m²
                        </div>
                      )}
                      <div style={{ marginTop: 12 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 9999, background: ddColor.bg, color: ddColor.color, border: `1px solid ${ddColor.dot}55`, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: ddColor.dot, display: 'inline-block', flexShrink: 0 }} />
                          {sd.daire?.daireDurumu || 'Satışta'}
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {/* Detaylar */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e67d21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                        <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                      </svg>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Detaylar</span>
                    </div>
                    <div style={{ border: '1px solid #f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
                      {([
                        { faIcon: 'fas fa-building',        iconColor: '#e67d21', label: 'Proje',    value: sd.daire?.projeAdi },
                        { faIcon: 'fas fa-th-large',        iconColor: '#3b82f6', label: 'Blok',     value: sd.daire?.blokAdi },
                        { faIcon: 'fas fa-layer-group',     iconColor: '#3b82f6', label: 'Kat',      value: sd.daire?.katAdi },
                        { faIcon: 'fas fa-compass',         iconColor: '#6b7280', label: 'Cephe',    value: sd.daire?.cepheAdi },
                        { faIcon: 'fas fa-ruler-combined',  iconColor: '#1a2440', label: 'Net m²',   value: sd.daire?.netM2 ? `${sd.daire.netM2} m²` : null },
                        { faIcon: 'fas fa-ruler',           iconColor: '#e67d21', label: 'Brüt m²',  value: sd.daire?.brutM2 ? `${sd.daire.brutM2} m²` : null },
                        { faIcon: 'fas fa-hashtag',         iconColor: '#3b82f6', label: 'Takip No', value: sd.daire?.takipNo },
                      ] as { faIcon: string; iconColor: string; label: string; value: string | null | undefined }[]).filter(item => item.value).map((item, i, arr) => (
                        <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <span style={{ fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                            <i className={item.faIcon} style={{ fontSize: 14, color: item.iconColor, width: 16, textAlign: 'center', flexShrink: 0 }} />
                            {item.label}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', textAlign: 'right', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sunum Bilgileri */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sunum Bilgileri</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <span style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Sunum Durumu</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, padding: '3px 10px', borderRadius: 9999,
                          background: sd.durum === 'Gösterildi' ? '#f0fdf4' : sd.durum === 'Beğenildi' ? '#eff6ff' : sd.durum === 'Reddedildi' ? '#fff1f2' : '#f1f5f9',
                          color: sd.durum === 'Gösterildi' ? '#15803d' : sd.durum === 'Beğenildi' ? '#1d4ed8' : sd.durum === 'Reddedildi' ? '#be123c' : '#475569',
                        }}>{sd.durum}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <span style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Müşteri Tepkisi</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: tepkiBadge.bg, color: tepkiBadge.color }}>
                          {sd.musteriTepkisi}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <span style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Eklenme Tarihi</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{formatDate(sd.eklenmeTarihi)}</span>
                      </div>
                      {sd.not && (
                        <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                          <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Not</div>
                          <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{sd.not}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer — Önceki / Sonraki */}
              <div style={{ padding: '12px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', flexShrink: 0 }}>
                <button
                  onClick={() => setDaireDetayIndex(i => i !== null ? i - 1 : i)}
                  disabled={!hasPrev}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                    background: hasPrev ? '#fff' : '#f8fafc', color: hasPrev ? '#1a2440' : '#d1d5db',
                    fontSize: 13, fontWeight: 500, cursor: hasPrev ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (hasPrev) { e.currentTarget.style.background = '#1a2440'; e.currentTarget.style.color = '#fff'; } }}
                  onMouseLeave={e => { if (hasPrev) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#1a2440'; } }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Önceki
                </button>
                <span style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 500 }}>{daireDetayIndex + 1} / {siraliDaireler.length}</span>
                <button
                  onClick={() => setDaireDetayIndex(i => i !== null ? i + 1 : i)}
                  disabled={!hasNext}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                    background: hasNext ? '#fff' : '#f8fafc', color: hasNext ? '#1a2440' : '#d1d5db',
                    fontSize: 13, fontWeight: 500, cursor: hasNext ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (hasNext) { e.currentTarget.style.background = '#1a2440'; e.currentTarget.style.color = '#fff'; } }}
                  onMouseLeave={e => { if (hasNext) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#1a2440'; } }}
                >
                  Sonraki
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Video Modal */}
      {showVideoModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.8)' }}
          onClick={() => setShowVideoModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#000', borderRadius: 12, maxWidth: 720, width: '90%', overflow: 'hidden', position: 'relative' }}>
            <button onClick={() => setShowVideoModal(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>×</button>
            {showVideoModal.sunumBelgeAdi && (
              <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: showVideoModal.sunumBelgeAdi }} />
            )}
            {showVideoModal.aciklama && (
              <div style={{ padding: '8px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{showVideoModal.aciklama}</div>
            )}
          </div>
        </div>
      )}

      {/* Ödeme Planı Modalı */}
      {showOdemePlaniModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowOdemePlaniModal(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)' }} />
          <div style={{ position: 'relative', width: '96%', maxWidth: 1360, maxHeight: '94vh', background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '18px 26px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #1a2440 0%, #232f52 100%)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(230,125,33,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="fas fa-file-invoice-dollar" style={{ fontSize: 18, color: '#e67d21' }}></i>
                </div>
                <div>
                  <div style={{ fontSize: 16.5, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
                    Ödeme Planı — {showOdemePlaniModal.baslik}
                  </div>
                  {showOdemePlaniModal.listeFiyati && (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                      Liste Fiyatı: {showOdemePlaniModal.listeFiyati.toLocaleString('tr-TR')} ₺
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => setShowOdemePlaniModal(null)}
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', width: 32, height: 32, color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.18)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}>
                <X size={17} />
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20, background: '#fbfbfd' }}>
              {odemePlaniLoading ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem 0' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                </div>
              ) : odemePlaniTeklifler[aktifTeklifTab] && (() => {
                const aktif = odemePlaniTeklifler[aktifTeklifTab];
                const ortalamaVade = calculateOrtalamaVadeGun(aktif.kalemler);
                const listeFiyati = showOdemePlaniModal?.listeFiyati || 0;
                const { onerilenFiyat, vadeFarkGun } = hesaplaVadeBazliOneri(aktif.kalemler, listeFiyati, fiyatlamaAyari);
                const fiyatSonuc = teklifFiyatKontrol(aktif);
                const yazdirKapali = teklifYazdiriliyor || fiyatSonuc.engelli;
                const thSt: React.CSSProperties = { padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
                const hucreSt: React.CSSProperties = { padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12.5, color: '#374151', outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' };
                const turRenk = (tur: KalemTuru) => tur === 'Pesinat' ? { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }
                  : tur === 'Kapora' ? { background: '#f5f3ff', color: '#6d28d9', borderColor: '#c4b5fd' } : {};
                return (
                <>
                  {/* Teklif Tabları — segmented pill bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 4, padding: 4, background: '#eef0f5', borderRadius: 12, flexWrap: 'wrap' }}>
                      {odemePlaniTeklifler.map((tk, i) => (
                        <div key={i} onClick={() => setAktifTeklifTab(i)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 9, cursor: 'pointer',
                            background: i === aktifTeklifTab ? '#fff' : 'transparent',
                            color: i === aktifTeklifTab ? '#1a2440' : '#64748b',
                            fontSize: 12.5, fontWeight: 600,
                            boxShadow: i === aktifTeklifTab ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                            transition: 'all 0.15s ease',
                          }}>
                          <span>{tk.teklifAdi}</span>
                          {odemePlaniTeklifler.length > 1 && (
                            <i className="fas fa-times" style={{ fontSize: 10, opacity: 0.6 }}
                              onClick={e => { e.stopPropagation(); handleTeklifSil(i); }} />
                          )}
                        </div>
                      ))}
                    </div>
                    <button onClick={handleTeklifEkle}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', border: '1.5px dashed #cbd5e1', borderRadius: 10, background: 'transparent', fontSize: 12.5, color: '#64748b', cursor: 'pointer', fontWeight: 600 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#e67d21'; e.currentTarget.style.color = '#e67d21'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}>
                      <i className="fas fa-plus" style={{ fontSize: 10 }}></i> Yeni Teklif
                    </button>
                  </div>

                  {/* Teklif özet kartı: Ad + Miktar + Vade + Yazdır */}
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', display: 'flex', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px', padding: '16px 20px' }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Teklif Adı</label>
                      <input type="text" value={aktif.teklifAdi}
                        onChange={e => updateAktifTeklif(t => ({ ...t, teklifAdi: e.target.value }))}
                        style={{ width: '100%', padding: '8px 0', border: 'none', borderBottom: '2px solid #f1f5f9', fontSize: 14.5, fontWeight: 600, color: '#1a2440', outline: 'none', boxSizing: 'border-box', background: 'transparent' }}
                        onFocus={e => (e.currentTarget.style.borderBottomColor = '#e67d21')}
                        onBlur={e => (e.currentTarget.style.borderBottomColor = '#f1f5f9')} />
                    </div>
                    <div style={{ flex: '1 1 220px', padding: '16px 20px', borderLeft: '1px solid #f1f5f9' }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                        <i className="fas fa-hand-holding-usd" style={{ marginRight: 5, color: '#e67d21' }}></i>Teklif Miktarı
                      </label>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <input
                          type="text"
                          value={formatCurrency(aktif.teklifMiktari)}
                          onChange={e => {
                            const yeniMiktar = parseCurrency(e.target.value);
                            updateAktifTeklif(t => {
                              const hedef = parseFloat(yeniMiktar) || 0;
                              const kalemler = t.kalemler.length > 0 && hedef > 0 ? redistributeKalemler(t.kalemler, hedef) : t.kalemler;
                              return { ...t, teklifMiktari: yeniMiktar, kalemler };
                            });
                          }}
                          placeholder={t('label.tutar')}
                          style={{ flex: 1, minWidth: 0, padding: 0, border: 'none', fontSize: 19, fontWeight: 700, color: '#e67d21', outline: 'none', background: 'transparent' }}
                        />
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#e67d21' }}>₺</span>
                      </div>
                    </div>
                    <div style={{ flex: '0 0 150px', padding: '16px 20px', borderLeft: '1px solid #f1f5f9' }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                        <i className="fas fa-clock" style={{ marginRight: 5, color: '#0369a1' }}></i>Ort. Vade
                      </label>
                      <div style={{ fontSize: 19, fontWeight: 700, color: '#0369a1' }}>{ortalamaVade} <span style={{ fontSize: 12, fontWeight: 600 }}>gün</span></div>
                    </div>
                    <div style={{ flex: '0 0 auto', padding: 12, display: 'flex', alignItems: 'center', gap: 8, borderLeft: '1px solid #f1f5f9' }}>
                      <button onClick={handleTeklifYazdirAc} disabled={yazdirKapali}
                        title={fiyatSonuc.engelli ? 'Ödeme planı proje fiyatlandırma kurallarına uymuyor' : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 18px', border: 'none', borderRadius: 11, background: yazdirKapali ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13, fontWeight: 600, cursor: yazdirKapali ? 'not-allowed' : 'pointer', transition: 'background 0.15s ease' }}
                        onMouseEnter={e => { if (!yazdirKapali) e.currentTarget.style.background = '#e67d21'; }}
                        onMouseLeave={e => { if (!yazdirKapali) e.currentTarget.style.background = '#1a2440'; }}>
                        <i className={teklifYazdiriliyor ? 'fas fa-spinner fa-spin' : 'fas fa-print'} style={{ fontSize: 12 }}></i>
                        {teklifYazdiriliyor ? 'Hazırlanıyor...' : 'Teklif Olarak Yazdır'}
                      </button>
                      {/* Sözleşme Yap yalnızca proje daireleri için — emlak portföyü öğelerinde sözleşme akışı yok */}
                      {showOdemePlaniModal.ogeTuru !== 'Portfoy' && (
                        <button onClick={handleSozlesmeYap} disabled={sozlesmeYapiliyor}
                          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 18px', border: 'none', borderRadius: 11, background: sozlesmeYapiliyor ? '#94a3b8' : '#059669', color: '#fff', fontSize: 13, fontWeight: 600, cursor: sozlesmeYapiliyor ? 'not-allowed' : 'pointer', transition: 'background 0.15s ease' }}
                          onMouseEnter={e => { if (!sozlesmeYapiliyor) e.currentTarget.style.background = '#047857'; }}
                          onMouseLeave={e => { if (!sozlesmeYapiliyor) e.currentTarget.style.background = '#059669'; }}>
                          <i className={sozlesmeYapiliyor ? 'fas fa-spinner fa-spin' : 'fas fa-file-signature'} style={{ fontSize: 12 }}></i>
                          {sozlesmeYapiliyor ? 'Hazırlanıyor...' : 'Sözleşme Yap'}
                        </button>
                      )}
                    </div>
                  </div>

                  <FiyatKontrolUyari sonuc={fiyatSonuc} engelMetni="Teklif yazdırma kapalı." onAraliklar={() => setShowFiyatAraliklari(true)} />
                  {showFiyatAraliklari && fiyatKurali && (
                    <FiyatAraliklariModal kural={fiyatKurali} listeFiyati={showOdemePlaniModal.listeFiyati} ortalamaVade={ortalamaVade}
                      onClose={() => setShowFiyatAraliklari(false)} />
                  )}

                  {!kuralAktifMi(fiyatKurali) && onerilenFiyat !== null && onerilenFiyat !== Math.round(parseFloat(aktif.teklifMiktari || '0')) && (
                    <div style={{ background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <i className="fas fa-chart-line" style={{ color: '#e67d21', fontSize: 18 }}></i>
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Vade Bazlı Fiyat Önerisi</div>
                        <div style={{ fontSize: 12, color: '#92400e', marginTop: 3, lineHeight: 1.5 }}>
                          Bu planın ortalama vadesi ({ortalamaVade} gün), kabul edilen vadeyi ({fiyatlamaAyari?.kabulEdilenOrtalamaVadeGun} gün) {vadeFarkGun} gün aşıyor.
                          Liste fiyatı {listeFiyati.toLocaleString('tr-TR')} ₺ üzerinden önerilen teklif fiyatı: <b>{onerilenFiyat.toLocaleString('tr-TR')} ₺</b>
                        </div>
                      </div>
                      <button
                        onClick={() => updateAktifTeklif(t => {
                          const kalemler = t.kalemler.length > 0 ? redistributeKalemler(t.kalemler, onerilenFiyat!) : t.kalemler;
                          return { ...t, teklifMiktari: String(onerilenFiyat), kalemler };
                        })}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 9, background: '#e67d21', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                      >
                        <i className="fas fa-check"></i> Teklife Uygula
                      </button>
                    </div>
                  )}

                  {/* Kalemler */}
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="fas fa-list-ul" style={{ color: '#3b82f6' }}></i>Ödeme Kalemleri
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {aktif.kalemler.length > 0 && (() => {
                          const toplamKalem = aktif.kalemler.reduce((s, k) => s + (parseFloat(k.miktar) || 0), 0);
                          const teklifNum = parseFloat(aktif.teklifMiktari || '0');
                          const fark = teklifNum - toplamKalem;
                          const renk = fark === 0 ? '#059669' : fark > 0 ? '#d97706' : '#ef4444';
                          return (
                            <div style={{ fontSize: 11.5, color: renk, fontWeight: 700, background: `${renk}14`, padding: '5px 10px', borderRadius: 8 }}>
                              Toplam: {toplamKalem.toLocaleString('tr-TR')} ₺
                              {teklifNum > 0 && <span style={{ marginLeft: 6, opacity: 0.85 }}>/ Fark: {fark >= 0 ? '+' : ''}{fark.toLocaleString('tr-TR')} ₺</span>}
                            </div>
                          );
                        })()}
                        {aktif.kalemler.length > 0 && (
                          <button onClick={handleEsitDagit}
                            title="Teklif miktarını, kilitli kalemler hariç, kalan kalemlere eşit dağıt"
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', border: '1px solid #bfdbfe', borderRadius: 8, background: '#eff6ff', color: '#2563eb', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                            <i className="fas fa-equals" style={{ fontSize: 10.5 }}></i> Eşit Dağıt
                          </button>
                        )}
                        {aktif.kalemler.length > 0 && (
                          <button onClick={handleKalemleriTemizle}
                            title="Tüm ödeme kalemlerini sil"
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2', color: '#dc2626', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                            <i className="fas fa-trash-alt" style={{ fontSize: 10.5 }}></i> Temizle
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ border: '1px solid #f1f5f9', borderRadius: 12, overflowX: 'auto' }}>
                      <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ ...thSt, width: 32 }}>#</th>
                            <th style={{ ...thSt, width: 140 }}>Vade Tarihi</th>
                            <th style={{ ...thSt, width: 170 }}>Miktar (₺)</th>
                            <th style={{ ...thSt, width: 115 }}>Tür</th>
                            <th style={{ ...thSt, width: 165 }}>Ödeme Türü</th>
                            <th style={thSt}>{t('label.aciklama')}</th>
                            <th style={{ ...thSt, width: 84 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {aktif.kalemler.map((k, i) => {
                            const tur = kalemTuruBul(k.aciklama);
                            return (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fbfbfd', borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '7px 10px', color: '#cbd5e1', fontWeight: 700 }}>{i + 1}</td>
                              <td style={{ padding: '5px 6px' }}>
                                <TrDateInput value={k.vadeTarihi}
                                  onChange={e => {
                                    const v = e.target.value;
                                    if (v && !tarihGirilebilir(v)) return;
                                    // Tarih tamamlandığında (blur / takvim) kalemler tarihe göre yeniden sıralanır
                                    updateAktifTeklif(t => {
                                      const ks = [...t.kalemler];
                                      ks[i] = { ...ks[i], vadeTarihi: v };
                                      return { ...t, kalemler: v ? planiDuzenle(ks) : ks };
                                    });
                                  }}
                                  style={tarihIzinliMi(fiyatKurali, k.vadeTarihi, user?.kullaniciID) ? hucreSt : { ...hucreSt, borderColor: '#ef4444', background: '#fef2f2', color: '#b91c1c' }}
                                />
                              </td>
                              <td style={{ padding: '5px 6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <input type="text" value={formatCurrency(k.miktar)}
                                    onChange={e => updateAktifTeklif(t => {
                                      const ks = [...t.kalemler];
                                      ks[i] = { ...ks[i], miktar: parseCurrency(e.target.value), kilitli: true };
                                      const hedef = parseFloat(t.teklifMiktari.replace(/[^\d.]/g, '')) || 0;
                                      return { ...t, kalemler: hedef > 0 ? redistributeKalemler(ks, hedef) : ks };
                                    })}
                                    placeholder="0"
                                    style={{ ...hucreSt, flex: 1, minWidth: 0 }}
                                  />
                                  <button type="button"
                                    onClick={() => updateKalem(i, { kilitli: !k.kilitli })}
                                    title={k.kilitli ? 'Kilidi kaldır (otomatik dağılıma dahil et)' : 'Kilitle (tutarı sabitle)'}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: k.kilitli ? '#e67d21' : '#cbd5e1', padding: 4, flexShrink: 0 }}>
                                    <i className={k.kilitli ? 'fas fa-lock' : 'fas fa-lock-open'} style={{ fontSize: 11.5 }}></i>
                                  </button>
                                </div>
                              </td>
                              <td style={{ padding: '5px 6px' }}>
                                <select value={tur}
                                  onChange={e => updateAktifTeklif(t => ({ ...t, kalemler: kalemTuruDegistir(t.kalemler, i, e.target.value as KalemTuru) }))}
                                  style={{ ...hucreSt, fontWeight: tur === 'Taksit' ? 400 : 700, ...turRenk(tur) }}>
                                  {KALEM_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '5px 6px' }}>
                                <select value={k.odemeTuru || 'Nakit'} onChange={e => updateKalem(i, { odemeTuru: e.target.value })} style={hucreSt}>
                                  {ODEME_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '5px 6px' }}>
                                <input type="text" value={k.aciklama}
                                  onChange={e => updateKalem(i, { aciklama: e.target.value })}
                                  placeholder="—"
                                  style={hucreSt}
                                />
                              </td>
                              <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                                <button onClick={() => updateAktifTeklif(t => ({ ...t, kalemler: planiDuzenle(t.kalemler.filter((_, idx) => idx !== i)) }))}
                                  title="Kalemi sil"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 4, borderRadius: 6 }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}>
                                  <i className="fas fa-times" style={{ fontSize: 13 }}></i>
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          {/* Kalem Ekle satırı — girilen kalem tarih sırasına göre yerine yerleşir */}
                          <tr style={{ background: '#f0f7ff', borderTop: '2px solid #dbeafe' }}>
                            <td style={{ padding: '7px 10px', color: '#3b82f6' }}><i className="fas fa-plus" style={{ fontSize: 11 }}></i></td>
                            <td style={{ padding: '7px 6px' }}>
                              <TrDateInput value={yeniKalem.vadeTarihi} onChange={e => setYeniKalem(p => ({ ...p, vadeTarihi: e.target.value }))} style={hucreSt} />
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <input type="text" value={formatCurrency(yeniKalem.miktar)} placeholder="Miktar"
                                onChange={e => setYeniKalem(p => ({ ...p, miktar: parseCurrency(e.target.value) }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleKalemEkle(); }}
                                style={hucreSt} />
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <select value={yeniKalem.tur} onChange={e => setYeniKalem(p => ({ ...p, tur: e.target.value as KalemTuru }))}
                                style={{ ...hucreSt, ...turRenk(yeniKalem.tur) }}>
                                {KALEM_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <select value={yeniKalem.odemeTuru} onChange={e => setYeniKalem(p => ({ ...p, odemeTuru: e.target.value }))} style={hucreSt}>
                                {ODEME_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <input type="text" value={yeniKalem.tur === 'Taksit' ? yeniKalem.aciklama : (yeniKalem.tur === 'Pesinat' ? 'Peşinat' : 'Kapora')}
                                disabled={yeniKalem.tur !== 'Taksit'}
                                placeholder="Boş bırakılırsa taksit sırasıyla adlandırılır"
                                onChange={e => setYeniKalem(p => ({ ...p, aciklama: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleKalemEkle(); }}
                                style={{ ...hucreSt, background: yeniKalem.tur !== 'Taksit' ? '#f8fafc' : '#fff' }} />
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <button type="button" onClick={handleKalemEkle}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, width: '100%', padding: '7px 10px', border: 'none', borderRadius: 7, background: '#3b82f6', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#2563eb')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#3b82f6')}>
                                <i className="fas fa-plus" style={{ fontSize: 10.5 }}></i> Ekle
                              </button>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Hazır Plan + Toplu Planlama, yan yana */}
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {/* Hazır Ödeme Planı */}
                    <div style={{ flex: '1 1 320px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', padding: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 8, background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-file-invoice" style={{ fontSize: 12, color: '#059669' }}></i>
                        </div>
                        Hazır Ödeme Planı Uygula
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 200px' }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Şablon</label>
                          <select value={seciliSablonId} onChange={e => setSeciliSablonId(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none', background: '#fff' }}>
                            <option value="">Şablon seçin...</option>
                            {odemePlanSablonlari.map(s => (
                              <option key={s.sablonId} value={s.sablonId}>{s.sablonAdi}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '0 1 170px' }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Ödeme Türü</label>
                          <select value={sablonOdemeTuru} onChange={e => setSablonOdemeTuru(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none', background: '#fff' }}>
                            {ODEME_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <button onClick={handleSablonUygula} disabled={!seciliSablonId}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 9, background: seciliSablonId ? '#059669' : '#e2e8f0', color: seciliSablonId ? '#fff' : '#94a3b8', fontSize: 13, fontWeight: 600, cursor: seciliSablonId ? 'pointer' : 'not-allowed', transition: 'background 0.15s ease' }}>
                          <i className="fas fa-check" style={{ fontSize: 12 }}></i> Uygula
                        </button>
                      </div>
                      {odemePlanSablonlari.length === 0 && (
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10 }}>Tanımlı ödeme planı şablonu yok (Ayarlar → Sistem Ayarları → Ödeme Planı Şablonları).</div>
                      )}
                    </div>

                    {/* Toplu Planlama */}
                    <div style={{ flex: '1 1 380px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', padding: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-magic" style={{ fontSize: 12, color: '#8b5cf6' }}></i>
                        </div>
                        Toplu Planlama
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Tutar (₺)</label>
                          <input type="text" value={formatCurrency(topluPlanTutari)} onChange={e => setTopluPlanTutari(parseCurrency(e.target.value))}
                            placeholder="0"
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, width: 110, outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Taksit</label>
                          <input type="number" min="1" value={topluTaksitSayisi} onChange={e => setTopluTaksitSayisi(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, width: 70, outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>İlk Vade</label>
                          <TrDateInput value={topluIlkTarih} onChange={e => setTopluIlkTarih(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Ödeme Türü</label>
                          <select value={topluOdemeTuru} onChange={e => setTopluOdemeTuru(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none', background: '#fff' }}>
                            {ODEME_TURLERI.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 110 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Açıklama</label>
                          <input type="text" value={topluAciklama} onChange={e => setTopluAciklama(e.target.value)}
                            placeholder={t('label.ara')}
                            style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none' }} />
                        </div>
                      </div>
                      <button onClick={handleTopluPlanOlustur}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 9, background: '#8b5cf6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#7c3aed')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#8b5cf6')}>
                        <i className="fas fa-magic" style={{ fontSize: 12 }}></i> Taksitleri Oluştur
                      </button>
                    </div>
                  </div>
                </>
                );
              })()}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fff', flexShrink: 0 }}>
              <button onClick={() => setShowOdemePlaniModal(null)}
                style={{ padding: '9px 20px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 600, color: '#475569' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                İptal
              </button>
              <button onClick={handleOdemePlaniSave} disabled={odemePlaniSaving || odemePlaniLoading || odemePlaniTeklifler.length === 0}
                style={{ padding: '9px 22px', border: 'none', borderRadius: 10, background: odemePlaniSaving ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13.5, cursor: odemePlaniSaving ? 'not-allowed' : 'pointer', fontWeight: 700, boxShadow: '0 2px 8px rgba(26,36,64,0.25)' }}
                onMouseEnter={e => { if (!odemePlaniSaving) e.currentTarget.style.background = '#e67d21'; }}
                onMouseLeave={e => { if (!odemePlaniSaving) e.currentTarget.style.background = '#1a2440'; }}>
                <i className="fas fa-save" style={{ marginRight: 6 }}></i>
                {odemePlaniSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teklif Yazdır — belgeye dahil edilecek bilgiler */}
      {teklifYazdirModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}
          onClick={() => setTeklifYazdirModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)' }} />
          <div onClick={e => e.stopPropagation()}
            style={{ position: 'relative', width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a2440', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-print" style={{ color: '#e67d21' }}></i> Teklif Olarak Yazdır
              </div>
              <button onClick={() => setTeklifYazdirModal(false)} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer' }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 12 }}>Teklif belgesine dahil edilecek bilgiler:</div>
              {([
                { key: 'listeFiyati', ad: 'Liste Fiyatı', aciklama: 'Teklif fiyatının yanında liste fiyatı gösterilir.' },
                { key: 'ortalamaVade', ad: 'Ortalama Vade', aciklama: 'Ödeme planının ortalama vadesi (gün) gösterilir.' },
              ] as { key: keyof TeklifYazdirSecenek; ad: string; aciklama: string }[]).map(o => (
                <label key={o.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: `1.5px solid ${teklifYazdirSecenek[o.key] ? '#e67d21' : '#e2e8f0'}`, background: teklifYazdirSecenek[o.key] ? '#fff7ed' : '#fff', borderRadius: 10, marginBottom: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={teklifYazdirSecenek[o.key]}
                    onChange={e => { const v = e.target.checked; setTeklifYazdirSecenek(prev => ({ ...prev, [o.key]: v })); }}
                    style={{ marginTop: 2, accentColor: '#e67d21', width: 16, height: 16 }} />
                  <span>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: '#1e293b' }}>{o.ad}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>{o.aciklama}</span>
                  </span>
                </label>
              ))}
            </div>
            <div style={{ padding: '12px 20px 18px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setTeklifYazdirModal(false)}
                style={{ padding: '9px 16px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Vazgeç
              </button>
              <button onClick={handleTeklifYazdir}
                style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: '#1a2440', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                <i className="fas fa-print" style={{ fontSize: 12 }}></i> Yazdır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Müşteri Linki Modalı */}
      {showLinkModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-link" style={{ fontSize: 15 }} />
                Müşteri Portal Linkleri
              </h2>
              <button onClick={() => setShowLinkModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: 6 }} onMouseEnter={e => (e.currentTarget.style.color = '#fff')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}>
                <i className="fas fa-times" style={{ fontSize: 18 }}></i>
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {linklerLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                  <div style={{ marginTop: 8, fontSize: 13 }}>{t('msg.yukleniyor')}</div>
                </div>
              ) : (
                <>
                  {/* Aktif Linkler */}
                  {linkler.length > 0 ? (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                        <i className="fas fa-list" style={{ marginRight: 6, color: '#3b82f6' }}></i>Aktif Linkler
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {linkler.map(link => (
                          <div key={link.linkID} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', background: link.sureGectiMi ? '#fef2f2' : '#f9fafb' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
                                <i className="far fa-calendar-alt" style={{ marginRight: 4 }}></i>
                                Oluşturulma: {new Date(link.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                {' • '}
                                <span style={{ color: link.sureGectiMi ? '#ef4444' : '#10b981' }}>
                                  Geçerlilik: {new Date(link.gecerlilikTarihi).toLocaleDateString('tr-TR')}
                                  {link.sureGectiMi && ' (Süresi Doldu)'}
                                </span>
                              </div>
                              {link.sureGectiMi && (
                                <span style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '3px 8px', borderRadius: 6, fontWeight: 600 }}>
                                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>Süresi Geçmiş
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 10 }}>
                              <i className="fas fa-link" style={{ fontSize: 12, color: '#94a3b8' }}></i>
                              <input type="text" value={link.portalUrl} readOnly style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12.5, color: '#475569', background: 'transparent' }} />
                              <button onClick={() => handleCopyLink(link.linkID, link.portalUrl)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: copiedLinkId === link.linkID ? '#10b981' : '#fff', color: copiedLinkId === link.linkID ? '#fff' : '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
                                <i className={copiedLinkId === link.linkID ? 'fas fa-check' : 'far fa-copy'} style={{ fontSize: 11 }}></i>
                                {copiedLinkId === link.linkID ? 'Kopyalandı' : 'Kopyala'}
                              </button>
                              <button
                                onClick={() => setQrVisibleLinkId(qrVisibleLinkId === link.linkID ? null : link.linkID)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: qrVisibleLinkId === link.linkID ? '#1a2440' : '#fff', color: qrVisibleLinkId === link.linkID ? '#fff' : '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                              >
                                <i className="fas fa-qrcode" style={{ fontSize: 12 }}></i>
                                QR Kod
                              </button>
                              {sunum.cari?.telefon1 && (() => {
                                const telefon = sunum.cari!.telefon1.replace(/\D/g, '');
                                const numara = telefon.startsWith('0') ? '90' + telefon.slice(1) : telefon.startsWith('90') ? telefon : '90' + telefon;
                                const mesaj = encodeURIComponent(`Merhaba ${sunum.cari!.adiSoyadi}, size özel hazırladığımız sunum için aşağıdaki bağlantıyı kullanabilirsiniz:\n${link.portalUrl}`);
                                return (
                                  <a
                                    href={`https://web.whatsapp.com/send?phone=${numara}&text=${mesaj}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', border: '1px solid #22c55e', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                                  >
                                    <i className="fab fa-whatsapp" style={{ fontSize: 13 }}></i>
                                    WhatsApp
                                  </a>
                                );
                              })()}
                            </div>
                            {qrVisibleLinkId === link.linkID && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '14px 0 6px', borderTop: '1px dashed #e2e8f0', marginTop: 4 }}>
                                <QRCodeSVG
                                  value={link.portalUrl}
                                  size={150}
                                  bgColor="#ffffff"
                                  fgColor="#1a2440"
                                  level="M"
                                  marginSize={2}
                                />
                                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>Kamera ile taratın</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              {link.sureGectiMi && (
                                <button onClick={() => handleExtendLink(link.linkID)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 12px', border: '1px solid #3b82f6', borderRadius: 7, background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }} onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}>
                                  <i className="fas fa-clock" style={{ fontSize: 11 }}></i> +3 Gün Uzat
                                </button>
                              )}
                              <button onClick={() => handleDeactivateLink(link.linkID)} style={{ flex: link.sureGectiMi ? 1 : 'initial', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 12px', border: '1px solid #f87171', borderRadius: 7, background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}>
                                <i className="fas fa-ban" style={{ fontSize: 11 }}></i> Devre Dışı Bırak
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>
                      <i className="fas fa-link" style={{ fontSize: 28, display: 'block', marginBottom: 8, opacity: 0.4 }}></i>
                      Henüz oluşturulmuş link yok.
                    </div>
                  )}

                  {/* Yeni Link Oluşturma */}
                  <div style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: '16px 18px', background: '#fafafa' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                      <i className="fas fa-plus-circle" style={{ marginRight: 6, color: '#10b981' }}></i>Yeni Link Oluştur
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>Geçerlilik Süresi (Gün)</label>
                        <input type="number" min="1" value={yeniLinkGun} onChange={e => setYeniLinkGun(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                      </div>
                      <button onClick={handleCreateLink} disabled={creatingLink} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: creatingLink ? '#94a3b8' : '#10b981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: creatingLink ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }} onMouseEnter={e => { if (!creatingLink) e.currentTarget.style.background = '#059669'; }} onMouseLeave={e => { if (!creatingLink) e.currentTarget.style.background = '#10b981'; }}>
                        <i className={creatingLink ? 'fas fa-spinner fa-spin' : 'fas fa-plus'} style={{ fontSize: 12 }}></i>
                        {creatingLink ? 'Oluşturuluyor...' : 'Oluştur'}
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                      <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                      Oluşturduğunuz link, belirlenen süre boyunca geçerli olacaktır. Müşteri bu linkle sunumu görüntüleyebilir ve tepki verebilir.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
              <button onClick={() => setShowLinkModal(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Döküman Paylaşım Modal */}
      {showDokumanPaylas && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.22)', width: '100%', maxWidth: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>Döküman Paylaş</h4>
              <button onClick={() => setShowDokumanPaylas(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <select
                value={dokumanEtiketFiltre}
                onChange={e => setDokumanEtiketFiltre(e.target.value === '' ? '' : parseInt(e.target.value))}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', minWidth: 180 }}
              >
                <option value=''>Tüm Etiketler</option>
                {tumEtiketler.map(et => (
                  <option key={et.etiketID} value={et.etiketID}>{et.ad}</option>
                ))}
              </select>
              <span style={{ marginLeft: 12, fontSize: 12.5, color: '#64748b' }}>{seciliDokumanIDs.length} döküman seçildi</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tumDokumanlar
                .filter(d => dokumanEtiketFiltre === '' || d.etiketler.some(e => e.etiketID === dokumanEtiketFiltre))
                .map(doc => {
                  const secili = seciliDokumanIDs.includes(doc.dokumanID);
                  return (
                    <label key={doc.dokumanID} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: `1px solid ${secili ? '#6366f1' : '#e2e8f0'}`, borderRadius: 9, background: secili ? '#f0f0ff' : '#fafafa', cursor: 'pointer', transition: 'all 0.12s' }}>
                      <input
                        type='checkbox'
                        checked={secili}
                        onChange={() => setSeciliDokumanIDs(prev => secili ? prev.filter(x => x !== doc.dokumanID) : [...prev, doc.dokumanID])}
                        style={{ marginTop: 3, accentColor: '#6366f1', cursor: 'pointer', width: 15, height: 15 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className={doc.dokumanTipi === 'Video' ? 'fas fa-film' : 'fas fa-file-alt'} style={{ color: '#6366f1', fontSize: 12 }} />
                          {doc.baslik}
                        </div>
                        {doc.aciklama && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{doc.aciklama}</div>}
                        {doc.etiketler?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                            {doc.etiketler.map((e, i) => (
                              <span key={i} style={{ background: e.renk || '#e2e8f0', color: '#fff', borderRadius: 5, padding: '0 6px', fontSize: 10.5 }}>{e.ad}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              {tumDokumanlar.filter(d => dokumanEtiketFiltre === '' || d.etiketler.some(e => e.etiketID === dokumanEtiketFiltre)).length === 0 && (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>Bu etikete ait döküman yok.</div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowDokumanPaylas(false)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer' }}>{t('btn.iptal')}</button>
              <button onClick={handleDokumanPaylasimKaydet} disabled={dokumanPaylasimSaving} style={{ padding: '8px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: dokumanPaylasimSaving ? 'not-allowed' : 'pointer', opacity: dokumanPaylasimSaving ? 0.75 : 1 }}>
                {dokumanPaylasimSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Döküman Görüntüleyici */}
      {dokumanBelgeViewer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <button onClick={() => setDokumanBelgeViewer(null)} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }}>×</button>
          {dokumanBelgeViewer.startsWith('VIDEO:') ? (
            <div style={{ width: '80%', maxWidth: 700 }} dangerouslySetInnerHTML={{ __html: dokumanBelgeViewer.replace('VIDEO:', '') }} />
          ) : (
            <iframe src={dokumanBelgeViewer} title='Döküman' style={{ width: '90vw', height: '88vh', border: 'none', borderRadius: 10 }} />
          )}
        </div>
      )}
    </div>
  );
};

export default MusteriSunumDetay;
